import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DashTile, DashSection, DashSegBar, DB_STYLE, downloadFile, FilterChipBar } from '../components/dashboard/DashKit';
import { useAuth } from '../context/AuthContext';
import { canStaffSection } from '../utils/staffAccess';
import { useTenantSafe } from '../context/TenantContext';
import LandUnitMap from '../components/land/LandUnitMap';
import CoordPickerModal from '../components/harvesting/CoordPickerModal';
import { useUrlParams } from '../hooks/useUrlParams';
import {
  getLandUnits,
  createLandUnit,
  getFactorSummary,
  addFactorObservation,
  updateFactorObservation,
  getFactorObservations,
  getScoreSnapshot,
  getNotes,
  addNote,
  getReviewHistory,
  setReviewStatus,
  getEvaluations,
  addEvaluation,
  getUnitReport,
} from '../services/landUnitService';

// ─── Constants (mirrors backend enums) ───────────────────────────────────────
const UNIT_TYPES = [
  { value: 'watershed',           label: 'Watershed' },
  { value: 'harvesting_zone',     label: 'Harvesting Zone' },
  { value: 'ecological_corridor', label: 'Ecological Corridor' },
  { value: 'parcel',              label: 'Parcel' },
  { value: 'cultural_area',       label: 'Cultural Area' },
  { value: 'seasonal_zone',       label: 'Seasonal Zone' },
  { value: 'project_footprint',   label: 'Project Footprint' },
  { value: 'custom',              label: 'Custom' },
];

const FACTOR_TYPES = [
  { value: 'harvesting_intensity',      label: 'Harvesting Intensity' },
  { value: 'seasonal_sensitivity',      label: 'Seasonal Sensitivity' },
  { value: 'traditional_knowledge_flag', label: 'Traditional Knowledge' },
  { value: 'consultation_concern',      label: 'Consultation Concern' },
  { value: 'community_priority',        label: 'Community Priority' },
  { value: 'ecological_condition',      label: 'Ecological Condition' },
  { value: 'cultural_significance',     label: 'Cultural Significance' },
  { value: 'access_sensitivity',        label: 'Access Sensitivity' },
  { value: 'manual',                    label: 'Manual Entry' },
];

const SOURCE_TYPES = [
  { value: 'harvest_record',            label: 'Harvest Record' },
  { value: 'questionnaire_response',    label: 'Consultation Response' },
  { value: 'land_value',                label: 'Land Value' },
  { value: 'community_data',            label: 'Community Data' },
  { value: 'staff_entry',               label: 'Staff Entry' },
  { value: 'import',                    label: 'Import' },
];

const FACTOR_COLOR = {
  harvesting_intensity:       '#22c55e',
  seasonal_sensitivity:       '#0ea5e9',
  traditional_knowledge_flag: '#a855f7',
  consultation_concern:       '#f59e0b',
  community_priority:         '#6366f1',
  ecological_condition:       '#10b981',
  cultural_significance:      '#ec4899',
  access_sensitivity:         '#f97316',
  manual:                     '#94a3b8',
};

const UNIT_TYPE_ICON = {
  watershed:           '💧',
  harvesting_zone:     '🌿',
  ecological_corridor: '🦅',
  parcel:              '📐',
  cultural_area:       '🏛️',
  seasonal_zone:       '🍂',
  project_footprint:   '🏗️',
  custom:              '📍',
};

const SCOPE_COLOR = { community: '#0ea5e9', platform: '#a855f7' };
const CONF_COLOR  = { low: '#f59e0b', medium: '#0ea5e9', high: '#22c55e' };

const REVIEW_STATUSES = [
  { value: 'open',         label: 'Open',         color: '#64748b' },
  { value: 'under_review', label: 'Under Review',  color: '#f59e0b' },
  { value: 'reviewed',     label: 'Reviewed',      color: '#22c55e' },
  { value: 'escalated',    label: 'Escalated',     color: '#ef4444' },
];
const REVIEW_COLOR = Object.fromEntries(REVIEW_STATUSES.map(r => [r.value, r.color]));

const EVAL_TYPES = [
  { value: 'preserve',           label: 'Preserve',              color: '#22c55e' },
  { value: 'monitor',            label: 'Monitor',               color: '#0ea5e9' },
  { value: 'consult_further',    label: 'Consult Further',       color: '#f59e0b' },
  { value: 'suitable_conditions',label: 'Suitable w/ Conditions',color: '#a855f7' },
  { value: 'unsuitable',         label: 'Unsuitable',            color: '#ef4444' },
  { value: 'no_concerns',        label: 'No Concerns',           color: '#64748b' },
];


// ─── Report markdown builder ──────────────────────────────────────────────────
function buildMarkdown(report) {
  const { unit, scoreSnapshot, evaluations, generatedAt, generatedBy } = report;
  const FACTOR_LABELS = {
    harvesting_intensity:        'Harvesting Intensity',
    seasonal_sensitivity:        'Seasonal Sensitivity',
    traditional_knowledge_flag:  'Traditional Knowledge',
    consultation_concern:        'Consultation Concern',
    community_priority:          'Community Priority',
    ecological_condition:        'Ecological Condition',
    cultural_significance:       'Cultural Significance',
    access_sensitivity:          'Access Sensitivity',
    manual:                      'Manual Entry',
  };
  const EVAL_LABELS = {
    preserve: 'Preserve', monitor: 'Monitor', consult_further: 'Consult Further',
    suitable_conditions: 'Suitable w/ Conditions', unsuitable: 'Unsuitable', no_concerns: 'No Concerns',
  };
  const REVIEW_LABELS = { open: 'Open', under_review: 'Under Review', reviewed: 'Reviewed', escalated: 'Escalated' };
  const pct = (v) => `${Math.round((v ?? 0) * 100)}%`;
  const fmt = (d) => d ? new Date(d).toLocaleDateString() : '—';

  const lines = [];
  lines.push(`# Land Unit Report: ${unit.name}`);
  lines.push(`Generated: ${new Date(generatedAt).toLocaleString()} · By: ${generatedBy}`);
  lines.push('');

  lines.push('## Unit Profile');
  lines.push(`- **Type:** ${UNIT_TYPES.find(t => t.value === unit.unitType)?.label || unit.unitType}`);
  lines.push(`- **Scope:** ${unit.scope}`);
  if (unit.areaHa) lines.push(`- **Area:** ${unit.areaHa.toLocaleString()} ha`);
  if (unit.isSensitive) lines.push(`- **Sensitive location:** Yes`);
  if (unit.tags?.length) lines.push(`- **Tags:** ${unit.tags.join(', ')}`);
  if (unit.externalId) lines.push(`- **External ID:** ${unit.externalId}`);
  if (unit.description) lines.push(`\n${unit.description}`);
  lines.push('');

  if (scoreSnapshot) {
    lines.push('## Score Snapshot');
    lines.push(`**Weighted score:** ${pct(scoreSnapshot.weightedScore)} (${scoreSnapshot.confidence} confidence, equal weights)`);
    lines.push('');
    lines.push('| Factor | Latest | Observations | Confidence | Contribution |');
    lines.push('|---|---|---|---|---|');
    scoreSnapshot.factorInputs.forEach(f => {
      const label = FACTOR_LABELS[f.factor] || f.factor;
      lines.push(`| ${label} | ${pct(f.latestValue)} | ${f.count} | ${f.confidence} | ${pct(f.contribution)} |`);
    });
    lines.push('');
  }

  if (evaluations?.length) {
    lines.push('## Staff Evaluations');
    evaluations.forEach((ev, i) => {
      const label = EVAL_LABELS[ev.recommendation] || ev.recommendation;
      lines.push(`### ${i === 0 ? '(Latest) ' : ''}${label} — ${ev.confidence} confidence`);
      lines.push(`${fmt(ev.reviewedAt)} · ${ev.reviewedByName}`);
      lines.push('');
      lines.push(ev.summary);
      if (ev.suggestedActions) lines.push(`\n*Suggested actions:* ${ev.suggestedActions}`);
      if (ev.citedFactors?.length) {
        lines.push(`\n*Cited factors:* ${ev.citedFactors.map(f => FACTOR_LABELS[f] || f).join(', ')}`);
      }
      if (ev.linkedReviewStatus && ev.linkedReviewStatus !== 'open') {
        lines.push(`*Review status at time of evaluation:* ${REVIEW_LABELS[ev.linkedReviewStatus] || ev.linkedReviewStatus}`);
      }
      lines.push('');
    });
  }

  if (unit.reviewHistory?.length) {
    lines.push('## Review History');
    unit.reviewHistory.forEach(h => {
      const from = REVIEW_LABELS[h.fromStatus] || h.fromStatus;
      const to   = REVIEW_LABELS[h.toStatus]   || h.toStatus;
      lines.push(`- ${fmt(h.changedAt)} · ${h.changedByName || 'Staff'}: ${from} → ${to}${h.comment ? ` — ${h.comment}` : ''}`);
    });
    lines.push('');
  }

  if (unit.notes?.length) {
    lines.push('## Staff Notes');
    unit.notes.forEach(n => {
      const vis = n.visibility === 'community' ? '[Community]' : '[Internal]';
      lines.push(`- ${vis} ${fmt(n.createdAt)} · ${n.authorName || 'Staff'}: ${n.text}`);
    });
    lines.push('');
  }

  return lines.join('\n');
}

// ─── Community-facing report builder ────────────────────────────────────────────
// Rules: mask TK rationale, community-visible notes only, no review history,
// no staff names, no internal evaluation details beyond the latest recommendation.
function buildCommunityMarkdown(report) {
  const { unit, scoreSnapshot, evaluations, generatedAt } = report;
  const FACTOR_LABELS = {
    harvesting_intensity:        'Harvesting Intensity',
    seasonal_sensitivity:        'Seasonal Sensitivity',
    traditional_knowledge_flag:  'Traditional Knowledge',
    consultation_concern:        'Consultation Concern',
    community_priority:          'Community Priority',
    ecological_condition:        'Ecological Condition',
    cultural_significance:       'Cultural Significance',
    access_sensitivity:          'Access Sensitivity',
    manual:                      'Manual Entry',
  };
  const EVAL_LABELS = {
    preserve: 'Preserve', monitor: 'Monitor', consult_further: 'Consult Further',
    suitable_conditions: 'Suitable w/ Conditions', unsuitable: 'Unsuitable', no_concerns: 'No Concerns',
  };
  const pct = (v) => `${Math.round((v ?? 0) * 100)}%`;
  const fmt = (d) => d ? new Date(d).toLocaleDateString() : '—';

  const lines = [];
  lines.push(`# Community Land Unit Summary: ${unit.name}`);
  lines.push(`Prepared: ${fmt(generatedAt)}`);
  lines.push('');
  lines.push('> This document is a community-facing summary prepared for information purposes.');
  lines.push('> Detailed internal assessments are held separately by the management team.');
  lines.push('');

  // Unit profile — mask sensitive geometry details
  lines.push('## Unit Overview');
  const typeLabel = UNIT_TYPES.find(t => t.value === unit.unitType)?.label || unit.unitType;
  lines.push(`- **Type:** ${typeLabel}`);
  if (unit.areaHa) lines.push(`- **Area:** ${unit.areaHa.toLocaleString()} ha`);
  if (unit.isSensitive) {
    lines.push('- ⚠ **Sensitive location** — specific geographic details are restricted.');
  }
  if (unit.tags?.length) lines.push(`- **Tags:** ${unit.tags.join(', ')}`);
  if (unit.description) lines.push(`\n${unit.description}`);
  lines.push('');

  // Score summary — factor bars without rationale; TK factor protected
  if (scoreSnapshot?.factorInputs?.length) {
    lines.push('## Assessment Summary');
    const score = Math.round((scoreSnapshot.weightedScore ?? 0) * 100);
    const tier  = score >= 70 ? 'Good standing' : score >= 40 ? 'Moderate concern' : 'Elevated concern';
    lines.push(`**Overall condition score:** ${pct(scoreSnapshot.weightedScore)} — ${tier}`);
    lines.push(`Confidence: ${scoreSnapshot.confidence}`);
    lines.push('');
    lines.push('| Factor | Status |');
    lines.push('|---|---|');
    scoreSnapshot.factorInputs.forEach(f => {
      const label  = FACTOR_LABELS[f.factor] || f.factor;
      const isTK   = f.factor === 'traditional_knowledge_flag';
      const status = isTK ? '(details protected)' : pct(f.latestValue);
      lines.push(`| ${label} | ${status} |`);
    });
    lines.push('');
  }

  // Latest recommendation only — no staff name
  const latestEval = evaluations?.[0];
  if (latestEval) {
    lines.push('## Management Recommendation');
    const recLabel = EVAL_LABELS[latestEval.recommendation] || latestEval.recommendation;
    lines.push(`**Recommendation:** ${recLabel}`);
    if (latestEval.summary) lines.push(`\n${latestEval.summary}`);
    if (latestEval.suggestedActions) lines.push(`\n*Suggested actions:* ${latestEval.suggestedActions}`);
    lines.push('');
  }

  // Community-visible notes only
  const communityNotes = (unit.notes || []).filter(n => n.visibility === 'community');
  if (communityNotes.length) {
    lines.push('## Community Notes');
    communityNotes.forEach(n => {
      lines.push(`- ${fmt(n.createdAt)}: ${n.text}`);
    });
    lines.push('');
  }

  lines.push('---');
  lines.push('*This summary was generated by the land management system. For questions, contact the community management team.*');

  return lines.join('\n');
}

// ─── Factor Value Bar ─────────────────────────────────────────────────────────
function FactorBar({ factor, avgValue, latestValue, count, confidence }) {
  const label = FACTOR_TYPES.find(f => f.value === factor)?.label || factor;
  const color = FACTOR_COLOR[factor] || '#94a3b8';
  const pct   = Math.round((latestValue ?? avgValue ?? 0) * 100);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12,
        color: '#94a3b8', marginBottom: 3 }}>
        <span style={{ color }}>{label}</span>
        <span style={{ color: '#e2e8f0' }}>
          {pct}% <span style={{ color: '#475569', fontSize: 11 }}>({count} obs, {confidence})</span>
        </span>
      </div>
      <div style={{ height: 5, background: '#0f172a', borderRadius: 3 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

// ─── Score gauge ──────────────────────────────────────────────────────────────
function ScoreGauge({ score, confidence }) {
  const pct     = Math.round((score ?? 0) * 100);
  const color   = pct >= 70 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#ef4444';
  const confCol = CONF_COLOR[confidence] || '#94a3b8';
  return (
    <div style={{ textAlign: 'center', marginBottom: 16 }}>
      <div style={{ fontSize: 38, fontWeight: 800, color, letterSpacing: -1 }}>
        {pct}<span style={{ fontSize: 20, fontWeight: 400, color: '#475569' }}>%</span>
      </div>
      <div style={{ height: 8, background: '#0f172a', borderRadius: 4, margin: '8px auto', maxWidth: 240 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
      {confidence && (
        <div style={{ fontSize: 11, color: confCol, marginTop: 4 }}>{confidence} confidence</div>
      )}
    </div>
  );
}

// ─── Score factor row (expandable drill-down) ─────────────────────────────────
function ScoreRow({ input, unitId, tenantId, isManager, profileActive, profileWeight }) {
  const [expanded,     setExpanded]     = useState(false);
  const [drillObs,     setDrillObs]     = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [editingId,    setEditingId]    = useState(null);
  const [editForm,     setEditForm]     = useState({});
  const [editSaving,   setEditSaving]   = useState(false);
  const [editErr,      setEditErr]      = useState('');

  const label   = FACTOR_TYPES.find(f => f.value === input.factor)?.label || input.factor;
  const color   = FACTOR_COLOR[input.factor] || '#94a3b8';
  const pct     = Math.round(input.latestValue * 100);
  const contrib = Math.round(input.contribution * 100);

  const SOURCE_LABEL = {
    harvest_record:         'Harvest Record',
    questionnaire_response: 'Consultation',
    land_value:             'Land Value',
    community_data:         'Community Data',
    staff_entry:            'Staff Entry',
    import:                 'Import',
  };

  // Known sourceTypes that map to a platform module route
  const SOURCE_ROUTE = {
    harvest_record:         '/harvesting',
    questionnaire_response: '/consultation',
    land_value:             '/land-values',
    community_data:         '/community-data',
  };

  const handleToggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && drillObs === null) {
      setDrillLoading(true);
      try {
        const params = { factor: input.factor };
        if (tenantId) params.tenantId = tenantId;
        const res = await getFactorObservations(unitId, params);
        setDrillObs(res.data || []);
      } catch {
        setDrillObs([]);
      } finally {
        setDrillLoading(false);
      }
    }
  };

  const openEdit = (obs) => {
    setEditingId(obs._id);
    setEditForm({ value: obs.value, confidence: obs.confidence, rationale: obs.rationale || '', reason: '' });
    setEditErr('');
  };

  const handleEditSubmit = async (obsId) => {
    setEditSaving(true);
    setEditErr('');
    try {
      const res = await updateFactorObservation(unitId, obsId, editForm);
      setDrillObs(prev => prev.map(o => o._id === obsId ? { ...o, ...res.data } : o));
      setEditingId(null);
    } catch (e) {
      setEditErr(e.response?.data?.message || 'Save failed');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div style={{ borderBottom: '1px solid #0f172a' }}>
      {/* Summary row — clickable */}
      <div
        onClick={handleToggle}
        title={input.rationale || 'Click to view source observations'}
        style={{
          display: 'flex', gap: 8, alignItems: 'center',
          padding: '7px 0', cursor: 'pointer',
        }}
      >
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <div style={{ flex: 1, fontSize: 12, color: '#cbd5e1' }}>{label}</div>
        {profileActive && profileWeight != null && (
          <div style={{ fontSize: 9, color: '#64748b', background: '#0f172a',
            borderRadius: 3, padding: '1px 4px', flexShrink: 0 }}>
            ×{profileWeight.toFixed(1)}
          </div>
        )}
        <div style={{ fontSize: 12, color: '#e2e8f0', width: 36, textAlign: 'right' }}>{pct}%</div>
        <div style={{ width: 80, height: 4, background: '#0f172a', borderRadius: 2 }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
        </div>
        <div style={{ fontSize: 11, color: '#475569', width: 52, textAlign: 'right' }}>+{contrib}% wt</div>
        {input.rationale && (
          <div style={{ fontSize: 10, color: '#334155' }}>ℹ</div>
        )}
        <div style={{ fontSize: 10, color: '#334155', userSelect: 'none' }}>
          {expanded ? '▲' : '▼'}
        </div>
      </div>

      {/* Drill-down: raw observations for this factor */}
      {expanded && (
        <div style={{ background: '#0a1628', borderRadius: 6, margin: '0 0 8px 18px', padding: '8px 10px' }}>
          {drillLoading && (
            <div style={{ fontSize: 11, color: '#475569' }}>Loading observations…</div>
          )}
          {!drillLoading && drillObs !== null && drillObs.length === 0 && (
            <div style={{ fontSize: 11, color: '#475569' }}>No raw observations found.</div>
          )}
          {!drillLoading && drillObs && drillObs.length > 0 && (
            <>
              <div style={{ fontSize: 9, color: '#334155', fontWeight: 700,
                letterSpacing: 1, marginBottom: 6 }}>
                {drillObs.length} OBSERVATION{drillObs.length !== 1 ? 'S' : ''}
              </div>
              {drillObs.map((obs, i) => {
                const obsPct    = Math.round((obs.value ?? 0) * 100);
                const confCol   = CONF_COLOR[obs.confidence] || '#94a3b8';
                const srcLabel  = SOURCE_LABEL[obs.sourceType] || obs.sourceType;
                const srcRoute  = SOURCE_ROUTE[obs.sourceType];
                const editCount = obs.editHistory?.length || 0;
                const obsDate   = obs.observedFrom
                  ? new Date(obs.observedFrom).toLocaleDateString()
                  : obs.createdAt
                    ? new Date(obs.createdAt).toLocaleDateString()
                    : '—';
                const isEditing = editingId === obs._id;

                return (
                  <div key={obs._id || i} style={{
                    padding: '5px 0',
                    borderBottom: i < drillObs.length - 1 ? '1px solid #0f172a' : 'none',
                  }}>
                    {/* Main obs row */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      {/* Value badge */}
                      <span style={{
                        background: color + '33', color: color,
                        borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 700, flexShrink: 0,
                      }}>{obsPct}%</span>
                      {/* Confidence dot */}
                      <span style={{ fontSize: 10, color: confCol, flexShrink: 0, marginTop: 2 }}>
                        ● {obs.confidence}
                      </span>
                      {/* Source chip — navigable link for known platform sources */}
                      {srcRoute ? (
                        <Link
                          to={srcRoute}
                          onClick={e => e.stopPropagation()}
                          style={{
                            background: '#0f3960', color: '#7dd3fc',
                            borderRadius: 4, padding: '1px 6px', fontSize: 10,
                            textDecoration: 'none', flexShrink: 0, cursor: 'pointer',
                          }}
                        >↗ {srcLabel}</Link>
                      ) : (
                        <span style={{
                          background: '#1e293b', color: '#64748b', fontSize: 10,
                          borderRadius: 4, padding: '1px 6px', flexShrink: 0,
                        }}>{srcLabel}</span>
                      )}
                      {/* Date */}
                      <span style={{ fontSize: 10, color: '#334155', flexShrink: 0 }}>{obsDate}</span>
                      {/* Created by */}
                      {obs.createdBy?.name && (
                        <span style={{ fontSize: 9, color: '#334155', flexShrink: 0, marginTop: 2 }}>
                          by {obs.createdBy.name}
                        </span>
                      )}
                      {/* Edit history badge */}
                      {editCount > 0 && (
                        <span style={{
                          fontSize: 9, color: '#f59e0b', flexShrink: 0,
                          background: '#78350f33', borderRadius: 4, padding: '1px 5px',
                        }}>edited {editCount}×</span>
                      )}
                      {/* Rationale */}
                      {obs.rationale && (
                        obs.rationale === '(protected — traditional knowledge)'
                          ? <span style={{ fontSize: 11, color: '#475569', flex: 1, minWidth: 80,
                              fontStyle: 'italic' }}>🔒 {obs.rationale}</span>
                          : <span style={{ fontSize: 11, color: '#64748b', flex: 1, minWidth: 80 }}>
                              {obs.rationale}
                            </span>
                      )}
                      {/* Manager edit button */}
                      {isManager && !isEditing && obs._id && (
                        <button
                          onClick={e => { e.stopPropagation(); openEdit(obs); }}
                          style={{
                            background: 'none', border: '1px solid #334155',
                            borderRadius: 3, color: '#64748b', fontSize: 9, padding: '1px 6px',
                            cursor: 'pointer', flexShrink: 0, marginLeft: 'auto',
                          }}
                        >Edit</button>
                      )}
                    </div>

                    {/* Inline edit form */}
                    {isEditing && (
                      <div style={{
                        background: '#0f2240', borderRadius: 6, marginTop: 6,
                        padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6,
                      }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <label style={{ fontSize: 10, color: '#94a3b8' }}>Value (0–1)</label>
                          <input
                            type="number"
                            min={0} max={1} step={0.01}
                            value={editForm.value}
                            onChange={e => setEditForm(f => ({ ...f, value: e.target.value }))}
                            style={{
                              width: 70, background: '#0a1628', border: '1px solid #1e3a5f',
                              color: '#e2e8f0', borderRadius: 4, padding: '2px 6px', fontSize: 11,
                            }}
                          />
                          <label style={{ fontSize: 10, color: '#94a3b8' }}>Confidence</label>
                          <select
                            value={editForm.confidence}
                            onChange={e => setEditForm(f => ({ ...f, confidence: e.target.value }))}
                            style={{
                              background: '#0a1628', border: '1px solid #1e3a5f',
                              color: '#e2e8f0', borderRadius: 4, padding: '2px 6px', fontSize: 11,
                            }}
                          >
                            <option value="low">low</option>
                            <option value="medium">medium</option>
                            <option value="high">high</option>
                          </select>
                        </div>
                        <textarea
                          rows={2}
                          placeholder="Rationale…"
                          value={editForm.rationale}
                          onChange={e => setEditForm(f => ({ ...f, rationale: e.target.value }))}
                          style={{
                            background: '#0a1628', border: '1px solid #1e3a5f', color: '#e2e8f0',
                            borderRadius: 4, padding: '4px 8px', fontSize: 11, resize: 'vertical',
                          }}
                        />
                        <textarea
                          rows={1}
                          placeholder="Reason for this edit (audit trail)…"
                          value={editForm.reason}
                          onChange={e => setEditForm(f => ({ ...f, reason: e.target.value }))}
                          style={{
                            background: '#0a1628', border: '1px solid #78350f', color: '#fbbf24',
                            borderRadius: 4, padding: '4px 8px', fontSize: 10, resize: 'vertical',
                          }}
                        />
                        {editErr && <div style={{ fontSize: 10, color: '#f87171' }}>{editErr}</div>}
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => handleEditSubmit(obs._id)}
                            disabled={editSaving}
                            style={{
                              background: '#1e3a5f', border: '1px solid #3b82f6', borderRadius: 4,
                              color: '#7dd3fc', padding: '3px 12px', cursor: 'pointer', fontSize: 11,
                            }}
                          >{editSaving ? 'Saving…' : 'Save'}</button>
                          <button
                            onClick={() => setEditingId(null)}
                            style={{
                              background: 'none', border: '1px solid #334155', borderRadius: 4,
                              color: '#64748b', padding: '3px 10px', cursor: 'pointer', fontSize: 11,
                            }}
                          >Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Score profile presets ──────────────────────────────────────────────────
// Weights are relative (will be normalised inside applyProfile).
// A weight of 0 means the factor is excluded from this profile's score.
const SCORE_PROFILES = [
  {
    id:    'equal',
    label: 'Equal Weight',
    desc:  'Every factor contributes equally. Default baseline.',
    color: '#64748b',
    weights: null, // null = equal split across all present factors
  },
  {
    id:    'ecology',
    label: 'Ecology Priority',
    desc:  'Doubles the weight of ecological condition, seasonal sensitivity and harvesting intensity.',
    color: '#22c55e',
    weights: {
      ecological_condition:   2.0,
      seasonal_sensitivity:   2.0,
      harvesting_intensity:   2.0,
      traditional_knowledge_flag: 1.0,
      consultation_concern:   1.0,
      community_priority:     1.0,
      cultural_significance:  1.0,
      access_sensitivity:     1.0,
      manual:                 0.5,
    },
  },
  {
    id:    'cultural',
    label: 'Cultural Priority',
    desc:  'Emphasises cultural significance, traditional knowledge and community priority.',
    color: '#ec4899',
    weights: {
      ecological_condition:   1.0,
      seasonal_sensitivity:   1.0,
      harvesting_intensity:   1.0,
      traditional_knowledge_flag: 2.0,
      consultation_concern:   1.5,
      community_priority:     2.0,
      cultural_significance:  2.0,
      access_sensitivity:     1.0,
      manual:                 0.5,
    },
  },
  {
    id:    'access',
    label: 'Access Sensitive',
    desc:  'Raises access sensitivity and consultation concern; reduces weight of ecological metrics.',
    color: '#f97316',
    weights: {
      ecological_condition:   0.5,
      seasonal_sensitivity:   1.0,
      harvesting_intensity:   1.0,
      traditional_knowledge_flag: 1.0,
      consultation_concern:   2.0,
      community_priority:     1.5,
      cultural_significance:  1.5,
      access_sensitivity:     3.0,
      manual:                 0.5,
    },
  },
];

/**
 * Re-weights a factorInputs array using a profile's weights map.
 * Returns { factorInputs (with overridden contribution/weight), weightedScore }.
 * The equal profile returns the original inputs unchanged.
 */
function applyProfile(factorInputs, profile) {
  if (!profile || profile.id === 'equal' || !profile.weights) {
    return { factorInputs, weightedScore: factorInputs.reduce((a, f) => a + f.contribution, 0) };
  }
  const wMap  = profile.weights;
  const items = factorInputs.map(f => ({
    ...f,
    weight: wMap[f.factor] ?? 1.0,
  }));
  const totalW = items.reduce((a, f) => a + f.weight, 0) || 1;
  const result = items.map(f => ({
    ...f,
    contribution: Math.round(f.latestValue * (f.weight / totalW) * 1000) / 1000,
  }));
  const weightedScore = Math.round(result.reduce((a, f) => a + f.contribution, 0) * 1000) / 1000;
  return { factorInputs: result, weightedScore };
}

// ─── Score snapshot panel ─────────────────────────────────────────────────────
function ScoreSnapshotPanel({ unit, tenantId, isManager }) {
  const [snap,      setSnap]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState('');
  const [copied,    setCopied]    = useState(false);
  const [profileId, setProfileId] = useState('equal');

  useEffect(() => {
    setLoading(true);
    setErr('');
    const params = tenantId ? { tenantId } : {};
    getScoreSnapshot(unit._id, params)
      .then(r => setSnap(r.data))
      .catch(() => setErr('Could not load snapshot'))
      .finally(() => setLoading(false));
  }, [unit._id, tenantId]);

  const activeProfile = SCORE_PROFILES.find(p => p.id === profileId) || SCORE_PROFILES[0];

  // Re-weight factors client-side according to the selected profile
  const profiled = snap?.factorInputs
    ? applyProfile(snap.factorInputs, activeProfile)
    : null;

  const displayScore   = profiled ? profiled.weightedScore : (snap?.weightedScore ?? null);
  const baselineScore  = snap?.weightedScore ?? null; // always equal-weight from backend
  const profileInputs  = profiled ? profiled.factorInputs : (snap?.factorInputs || []);

  const handleCopy = () => {
    if (!snap) return;
    const profileLabel = activeProfile.label;
    const lines = [
      `Land Unit Score Snapshot`,
      `Unit: ${unit.name}`,
      `Profile: ${profileLabel}`,
      `Score: ${Math.round((displayScore ?? 0) * 100)}%  (${snap.confidence} confidence)`,
      profileId !== 'equal' ? `Baseline (equal weight): ${Math.round((baselineScore ?? 0) * 100)}%` : null,
      `Factors: ${snap.factorCount}  |  Observations: ${snap.observationCount}`,
      `Generated: ${snap.computedAt ? new Date(snap.computedAt).toLocaleString() : '—'}`,
      '',
      'Factor Breakdown:',
      ...profileInputs.map(inp => {
        const label   = FACTOR_TYPES.find(f => f.value === inp.factor)?.label || inp.factor;
        const valPct  = Math.round(inp.latestValue * 100);
        const contrib = Math.round(inp.contribution * 100);
        const wt      = profileId !== 'equal' ? ` [wt ${inp.weight.toFixed(1)}]` : '';
        return `  ${label}: ${valPct}%${wt}  (contrib ${contrib}%)${inp.rationale ? '  — ' + inp.rationale : ''}`;
      }),
    ].filter(Boolean);
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <div style={{ color: '#475569', fontSize: 12, padding: '20px 0' }}>Computing…</div>;
  if (err)     return <div style={{ color: '#f87171', fontSize: 12, padding: '10px 0' }}>{err}</div>;
  if (!snap)   return null;

  if (snap.factorCount === 0) {
    return (
      <div style={{ color: '#475569', fontSize: 13, padding: '24px 0', textAlign: 'center' }}>
        {snap.message || 'No factor observations yet.'}
      </div>
    );
  }

  const computedAt = snap.computedAt ? new Date(snap.computedAt).toLocaleString() : '';

  return (
    <div>
      {/* Profile selector pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {SCORE_PROFILES.map(p => (
          <button
            key={p.id}
            onClick={() => setProfileId(p.id)}
            title={p.desc}
            style={{
              background: profileId === p.id ? p.color + '22' : 'transparent',
              border:     `1px solid ${profileId === p.id ? p.color : '#1e293b'}`,
              borderRadius: 20, color: profileId === p.id ? p.color : '#475569',
              padding: '3px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 600,
              transition: 'all 0.15s',
            }}
          >{p.label}</button>
        ))}
        {profileId !== 'equal' && (
          <span style={{ fontSize: 10, color: '#334155', alignSelf: 'center', fontStyle: 'italic' }}>
            {activeProfile.desc}
          </span>
        )}
      </div>

      {/* Score gauge — shows profiled score, muted baseline comparison */}
      <ScoreGauge score={displayScore} confidence={snap.confidence} />
      {profileId !== 'equal' && (
        <div style={{ textAlign: 'center', marginTop: -8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: '#334155' }}>
            Baseline (equal weight):
          </span>{' '}
          <span style={{ fontSize: 12, fontWeight: 700,
            color: Math.round((baselineScore ?? 0) * 100) >= 70 ? '#22c55e'
                 : Math.round((baselineScore ?? 0) * 100) >= 40 ? '#f59e0b' : '#ef4444',
          }}>
            {Math.round((baselineScore ?? 0) * 100)}%
          </span>
        </div>
      )}

      {/* Summary strip */}
      <div style={{ background: '#0f172a', borderRadius: 6, padding: '8px 12px',
        marginBottom: 12, fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
        <strong style={{ color: activeProfile.color }}>{ activeProfile.label }</strong> —{' '}
        {snap.factorCount} factor{snap.factorCount !== 1 ? 's' : ''},{' '}
        {snap.observationCount} observation{snap.observationCount !== 1 ? 's' : ''}.
        Scores are not stored — computed fresh on load.
        {computedAt && <span style={{ color: '#334155' }}> · {computedAt}</span>}
      </div>

      {/* Factor rows — click to expand observations */}
      <div style={{ marginBottom: 8, fontSize: 10, color: '#334155' }}>
        Click a factor row to view source observations ↓
        {profileId !== 'equal' && (
          <span style={{ marginLeft: 6, color: activeProfile.color }}>
            · weights active: {activeProfile.label}
          </span>
        )}
      </div>
      <div style={{ marginBottom: 4 }}>
        {profileInputs.map(inp => (
          <ScoreRow
            key={inp.factor}
            input={inp}
            unitId={unit._id}
            tenantId={tenantId}
            isManager={isManager}
            profileActive={profileId !== 'equal'}
            profileWeight={profileId !== 'equal' ? (inp.weight) : null}
          />
        ))}
      </div>

      {/* Footer: confidence legend + weights label + copy */}
      <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {Object.entries(CONF_COLOR).map(([k, c]) => (
          <span key={k} style={{ fontSize: 10, color: c }}>● {k}</span>
        ))}
        <span style={{ fontSize: 10, color: '#334155', marginLeft: 'auto' }}>
          weights: {activeProfile.label.toLowerCase()}
        </span>
        <button
          onClick={handleCopy}
          title="Copy snapshot summary to clipboard"
          style={{
            background: copied ? '#14532d' : '#1e293b',
            border: `1px solid ${copied ? '#22c55e' : '#334155'}`,
            color: copied ? '#4ade80' : '#64748b',
            borderRadius: 5, padding: '4px 10px', cursor: 'pointer',
            fontSize: 10, fontWeight: 600, transition: 'all 0.2s',
          }}
        >
          {copied ? '✓ Copied' : '📋 Copy'}
        </button>
      </div>
    </div>
  );
}

// ─── Unit Card ────────────────────────────────────────────────────────────────
function UnitCard({ unit, isManager, onSelect, isSelected }) {
  const icon = UNIT_TYPE_ICON[unit.unitType] || '📍';
  const scopeColor = SCOPE_COLOR[unit.scope] || '#94a3b8';
  const recColor = unit.latestRecommendation
    ? (EVAL_TYPES.find(e => e.value === unit.latestRecommendation)?.color || '#334155')
    : null;
  return (
    <div
      onClick={onSelect}
      style={{
        background: isSelected ? '#1e3a5f' : '#1e293b',
        border: `1px solid ${isSelected ? '#3b82f6' : '#334155'}`,
        borderLeft: recColor ? `3px solid ${recColor}` : `3px solid ${isSelected ? '#3b82f6' : '#1e293b'}`,
        borderRadius: 8, padding: '14px 16px', cursor: 'pointer',
        marginBottom: 8, transition: 'all 0.15s',
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 22, flexShrink: 0 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {unit.name}
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            {UNIT_TYPES.find(t => t.value === unit.unitType)?.label || unit.unitType}
            {unit.areaHa ? ` · ${unit.areaHa.toLocaleString()} ha` : ''}
          </div>
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, color: scopeColor,
          background: '#0f172a', borderRadius: 4, padding: '2px 6px', flexShrink: 0 }}>
          {unit.scope}
        </span>
        {unit.reviewStatus && unit.reviewStatus !== 'open' && (
          <span style={{
            fontSize: 9, fontWeight: 700,
            color: REVIEW_COLOR[unit.reviewStatus] || '#64748b',
            background: (REVIEW_COLOR[unit.reviewStatus] || '#64748b') + '22',
            borderRadius: 4, padding: '2px 6px', flexShrink: 0,
          }}>
            {REVIEW_STATUSES.find(r => r.value === unit.reviewStatus)?.label || unit.reviewStatus}
          </span>
        )}
        {unit.latestRecommendation && (() => {
          const et = EVAL_TYPES.find(e => e.value === unit.latestRecommendation);
          return et ? (
            <span style={{
              fontSize: 9, fontWeight: 700, color: et.color,
              background: et.color + '22', borderRadius: 4, padding: '2px 6px', flexShrink: 0,
            }}>{et.label}</span>
          ) : null;
        })()}
      </div>
      {unit.description && (
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {unit.description}
        </div>
      )}
    </div>
  );
}

// ─── Create Unit Form ─────────────────────────────────────────────────────────
function CreateUnitForm({ tenantId, isPlatformStaff, onCreated, onCancel }) {
  const [form, setForm] = useState({
    name: '', description: '', unitType: 'custom',
    scope: 'community',
    geoType: 'Point', lat: '', lng: '',
    geojsonText: '',
    areaHa: '', tags: '',
    isSensitive: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) { setGpsError('Geolocation not supported'); return; }
    setGpsLoading(true); setGpsError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set('lat', pos.coords.latitude.toFixed(6));
        set('lng', pos.coords.longitude.toFixed(6));
        setGpsLoading(false);
      },
      () => { setGpsError('Could not get location'); setGpsLoading(false); }
    );
  };

  const handlePickerConfirm = (lat, lng) => {
    if (lat !== '' && lng !== '') {
      set('lat', parseFloat(lat).toFixed(6));
      set('lng', parseFloat(lng).toFixed(6));
    }
    setPickerOpen(false);
  };

  const buildGeometry = () => {
    if (form.geoType === 'Point') {
      const lat = parseFloat(form.lat);
      const lng = parseFloat(form.lng);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { type: 'Point', coordinates: [lng, lat] };
    }
    try { return JSON.parse(form.geojsonText); } catch { return null; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    const geometry = buildGeometry();
    if (!geometry) { setErr('Invalid geometry'); return; }
    setSaving(true);
    try {
      const res = await createLandUnit({
        tenantId: form.scope === 'community' ? tenantId : null,
        name: form.name,
        description: form.description,
        unitType: form.unitType,
        scope: form.scope,
        geometry,
        areaHa: form.areaHa ? parseFloat(form.areaHa) : null,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        isSensitive: form.isSensitive,
      });
      onCreated(res.data);
    } catch (e2) {
      setErr(e2.response?.data?.message || 'Failed to create');
    } finally { setSaving(false); }
  };

  const inp = { background:'#0f172a', border:'1px solid #334155', borderRadius:6,
    color:'#e2e8f0', padding:'7px 10px', fontSize:13, width:'100%', boxSizing:'border-box' };

  return (
    <>
    <form onSubmit={handleSubmit} style={{ background:'#1e293b', borderRadius:10, padding:20, marginBottom:16 }}>
      <div style={{ fontSize:14, fontWeight:700, color:'#f1f5f9', marginBottom:14 }}>+ New Land Unit</div>
      {err && <div style={{ color:'#f87171', fontSize:12, marginBottom:10 }}>{err}</div>}

      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:10 }}>
        <div style={{ flex:'2 1 200px' }}>
          <div style={{ color:'#94a3b8', fontSize:11, marginBottom:4 }}>NAME *</div>
          <input style={inp} value={form.name} onChange={e=>set('name',e.target.value)} required />
        </div>
        <div style={{ flex:'1 1 140px' }}>
          <div style={{ color:'#94a3b8', fontSize:11, marginBottom:4 }}>TYPE</div>
          <select style={inp} value={form.unitType} onChange={e=>set('unitType',e.target.value)}>
            {UNIT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        {isPlatformStaff && (
          <div style={{ flex:'1 1 120px' }}>
            <div style={{ color:'#94a3b8', fontSize:11, marginBottom:4 }}>SCOPE</div>
            <select style={inp} value={form.scope} onChange={e=>set('scope',e.target.value)}>
              <option value="community">Community</option>
              <option value="platform">Platform</option>
            </select>
          </div>
        )}
      </div>

      <div style={{ marginBottom:10 }}>
        <div style={{ color:'#94a3b8', fontSize:11, marginBottom:4 }}>DESCRIPTION</div>
        <textarea style={{ ...inp, resize:'vertical', minHeight:56 }}
          value={form.description} onChange={e=>set('description',e.target.value)} />
      </div>

      <div style={{ marginBottom:10 }}>
        <div style={{ color:'#94a3b8', fontSize:11, marginBottom:6 }}>GEOMETRY *</div>
        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
          {['Point','Polygon','LineString'].map(gt => (
            <button key={gt} type="button" onClick={()=>set('geoType',gt)} style={{
              background: form.geoType===gt ? '#1e3a5f' : '#0f172a',
              border:`1px solid ${form.geoType===gt ?'#3b82f6':'#334155'}`,
              color: form.geoType===gt ?'#7dd3fc':'#94a3b8',
              borderRadius:5, padding:'4px 10px', cursor:'pointer', fontSize:12 }}>
              {gt}
            </button>
          ))}
        </div>
        {form.geoType === 'Point' ? (
          <>
            <div style={{ display:'flex', gap:8, marginBottom:6 }}>
              <button type="button" onClick={handleUseMyLocation} disabled={gpsLoading} style={{
                background:'#0f172a', border:'1px solid #334155', borderRadius:5,
                color:'#7dd3fc', padding:'5px 11px', cursor:'pointer', fontSize:12 }}>
                {gpsLoading ? '…' : '📍 Use My Location'}
              </button>
              <button type="button" onClick={() => setPickerOpen(true)} style={{
                background:'#0f172a', border:'1px solid #334155', borderRadius:5,
                color:'#7dd3fc', padding:'5px 11px', cursor:'pointer', fontSize:12 }}>
                🗺 Pick on Map
              </button>
              {form.lat && form.lng && (
                <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ background:'#1e3a5f', border:'1px solid #3b82f6', borderRadius:5,
                    padding:'3px 9px', fontSize:11, color:'#7dd3fc' }}>
                    {parseFloat(form.lat).toFixed(5)}, {parseFloat(form.lng).toFixed(5)}
                  </span>
                  <button type="button" onClick={() => { set('lat',''); set('lng',''); }} style={{
                    background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:13 }}>✕</button>
                </span>
              )}
            </div>
            {gpsError && <div style={{ color:'#f87171', fontSize:11, marginBottom:4 }}>{gpsError}</div>}
            <div style={{ display:'flex', gap:8 }}>
              <input style={{ ...inp, flex:1 }} placeholder="Latitude" type="number" step="any"
                value={form.lat} onChange={e=>set('lat',e.target.value)} />
              <input style={{ ...inp, flex:1 }} placeholder="Longitude" type="number" step="any"
                value={form.lng} onChange={e=>set('lng',e.target.value)} />
            </div>
          </>
        ) : (
          <textarea style={{ ...inp, fontFamily:'monospace', fontSize:11, resize:'vertical', minHeight:72 }}
            placeholder={`Paste GeoJSON geometry object:\n{ "type": "${form.geoType}", "coordinates": [...] }`}
            value={form.geojsonText} onChange={e=>set('geojsonText',e.target.value)} />
        )}
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:14 }}>
        <div style={{ flex:1 }}>
          <div style={{ color:'#94a3b8', fontSize:11, marginBottom:4 }}>AREA (ha)</div>
          <input style={inp} type="number" step="0.01" value={form.areaHa} onChange={e=>set('areaHa',e.target.value)} />
        </div>
        <div style={{ flex:2 }}>
          <div style={{ color:'#94a3b8', fontSize:11, marginBottom:4 }}>TAGS (comma-separated)</div>
          <input style={inp} value={form.tags} onChange={e=>set('tags',e.target.value)} />
        </div>
      </div>

      {/* Sensitive location flag */}
      <label style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, cursor:'pointer' }}>
        <input
          type="checkbox"
          checked={form.isSensitive}
          onChange={e => set('isSensitive', e.target.checked)}
          style={{ accentColor:'#f59e0b', width:14, height:14 }}
        />
        <span style={{ fontSize:12, color:'#94a3b8' }}>
          🔒 Sensitive location — reduces map coordinate precision for non-managers
        </span>
      </label>

      <div style={{ display:'flex', gap:8 }}>
        <button type="submit" disabled={saving} style={{
          background:'#1d4ed8', border:'none', borderRadius:6, color:'#fff',
          padding:'8px 18px', cursor:'pointer', fontSize:13, fontWeight:600 }}>
          {saving ? 'Creating…' : 'Create Unit'}
        </button>
        <button type="button" onClick={onCancel} style={{
          background:'#1e293b', border:'1px solid #334155', borderRadius:6,
          color:'#94a3b8', padding:'8px 14px', cursor:'pointer', fontSize:13 }}>
          Cancel
        </button>
      </div>
    </form>
    {pickerOpen && (
      <CoordPickerModal
        initLat={form.lat || ''}
        initLng={form.lng || ''}
        onConfirm={handlePickerConfirm}
        onClose={() => setPickerOpen(false)}
      />
    )}
    </>
  );
}

// ─── Add Observation Form ─────────────────────────────────────────────────────
function AddObservationForm({ unit, tenantId, onAdded, onCancel }) {
  const [form, setForm] = useState({
    factor:'harvesting_intensity', sourceType:'staff_entry', sourceRef:'',
    value:'0.5', confidence:'medium', rationale:'', season:'', sourceCount:'1',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    const val = parseFloat(form.value);
    if (isNaN(val) || val < 0 || val > 1) { setErr('value must be 0–1'); return; }
    setSaving(true);
    try {
      const res = await addFactorObservation(unit._id, {
        tenantId,
        factor:      form.factor,
        sourceType:  form.sourceType,
        sourceRef:   form.sourceRef || null,
        value:       val,
        confidence:  form.confidence,
        rationale:   form.rationale,
        season:      form.season || '',
        sourceCount: parseInt(form.sourceCount, 10) || 1,
      });
      onAdded(res.data);
    } catch (e2) {
      setErr(e2.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const inp = { background:'#0f172a', border:'1px solid #334155', borderRadius:5,
    color:'#e2e8f0', padding:'6px 8px', fontSize:12, width:'100%', boxSizing:'border-box' };

  return (
    <form onSubmit={handleSubmit} style={{ background:'#0f172a', borderRadius:8, padding:14, marginTop:10 }}>
      <div style={{ fontSize:12, fontWeight:700, color:'#7dd3fc', marginBottom:10 }}>Add Factor Observation</div>
      {err && <div style={{ color:'#f87171', fontSize:11, marginBottom:8 }}>{err}</div>}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
        <div style={{ flex:'1 1 160px' }}>
          <div style={{ color:'#64748b', fontSize:10, marginBottom:3 }}>FACTOR *</div>
          <select style={inp} value={form.factor} onChange={e=>set('factor',e.target.value)}>
            {FACTOR_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>

      {/* TK protection notice */}
      {form.factor === 'traditional_knowledge_flag' && (
        <div style={{
          background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.25)',
          borderRadius:6, padding:'6px 10px', fontSize:11, color:'#f59e0b',
          width:'100%', marginTop:4,
        }}>
          🔒 Rationale for Traditional Knowledge observations is protected. Non-managers will see a placeholder instead of the text you enter below.
        </div>
      )}
        <div style={{ flex:'1 1 140px' }}>
          <div style={{ color:'#64748b', fontSize:10, marginBottom:3 }}>SOURCE *</div>
          <select style={inp} value={form.sourceType} onChange={e=>set('sourceType',e.target.value)}>
            {SOURCE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div style={{ flex:'0 0 80px' }}>
          <div style={{ color:'#64748b', fontSize:10, marginBottom:3 }}>VALUE (0–1) *</div>
          <input style={inp} type="number" step="0.01" min="0" max="1"
            value={form.value} onChange={e=>set('value',e.target.value)} />
        </div>
        <div style={{ flex:'0 0 90px' }}>
          <div style={{ color:'#64748b', fontSize:10, marginBottom:3 }}>CONFIDENCE</div>
          <select style={inp} value={form.confidence} onChange={e=>set('confidence',e.target.value)}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom:8 }}>
        <div style={{ color:'#64748b', fontSize:10, marginBottom:3 }}>RATIONALE</div>
        <textarea style={{ ...inp, resize:'vertical', minHeight:44 }}
          value={form.rationale} onChange={e=>set('rationale',e.target.value)} />
      </div>

      <div style={{ display:'flex', gap:8 }}>
        <button type="submit" disabled={saving} style={{
          background:'#1d4ed8', border:'none', borderRadius:5, color:'#fff',
          padding:'6px 14px', cursor:'pointer', fontSize:12, fontWeight:600 }}>
          {saving ? 'Saving…' : 'Record'}
        </button>
        <button type="button" onClick={onCancel} style={{
          background:'transparent', border:'1px solid #334155', borderRadius:5,
          color:'#64748b', padding:'6px 10px', cursor:'pointer', fontSize:12 }}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Unit Detail Panel ────────────────────────────────────────────────────────
function UnitDetail({ unit, tenantId, isManager }) {
  const [detailTab,      setDetailTab]      = useState('factors'); // 'factors'|'snapshot'|'notes'|'review'
  const [summary,        setSummary]        = useState(null);
  const [loadingFac,     setLoadingFac]     = useState(true);
  const [showAddObs,     setShowAddObs]     = useState(false);
  const [factorFilter,   setFactorFilter]   = useState('');
  // Notes tab state
  const [notes,          setNotes]          = useState(null);
  const [loadingNotes,   setLoadingNotes]   = useState(false);
  const [noteText,       setNoteText]       = useState('');
  const [noteVis,        setNoteVis]        = useState('internal');
  const [savingNote,     setSavingNote]     = useState(false);
  const [noteErr,        setNoteErr]        = useState('');
  // Review tab state
  const [reviewStatus,   setReviewStatus_]  = useState(unit.reviewStatus || 'open');
  const [reviewHistory,  setReviewHistory]  = useState(null);
  const [loadingReview,  setLoadingReview]  = useState(false);
  const [reviewComment,  setReviewComment]  = useState('');
  const [reviewTarget,   setReviewTarget]   = useState('');
  const [savingReview,   setSavingReview]   = useState(false);
  const [reviewErr,      setReviewErr]      = useState('');

  // Report state
  const [showReport,    setShowReport]    = useState(false);
  const [report,        setReport]        = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportErr,     setReportErr]     = useState('');
  const [copied,        setCopied]        = useState(false);
  const [downloaded,          setDownloaded]          = useState(false);
  const [communityDownloaded, setCommunityDownloaded] = useState(false);

  // Evaluation state
  const [evaluations,    setEvaluations]    = useState(null);
  const [loadingEval,    setLoadingEval]    = useState(false);
  const [showAddEval,    setShowAddEval]    = useState(false);
  const [evalForm,       setEvalForm]       = useState({
    recommendation: '', summary: '', citedFactors: [],
    suggestedActions: '', confidence: 'medium',
  });
  const [savingEval,     setSavingEval]     = useState(false);
  const [evalErr,        setEvalErr]        = useState('');

  const loadSummary = useCallback(async () => {
    setLoadingFac(true);
    try {
      const res = await getFactorSummary(unit._id, tenantId ? { tenantId } : undefined);
      setSummary(res.data.summary || []);
    } catch { setSummary([]); } finally { setLoadingFac(false); }
  }, [unit._id, tenantId]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadNotes = useCallback(async () => {
    setLoadingNotes(true);
    try {
      const res = await getNotes(unit._id);
      setNotes(res.data.notes || []);
    } catch { setNotes([]); } finally { setLoadingNotes(false); }
  }, [unit._id]);

  // Load notes when notes tab first activated
  const loadReview = useCallback(async () => {
    setLoadingReview(true);
    try {
      const res = await getReviewHistory(unit._id);
      setReviewStatus_(res.data.reviewStatus || 'open');
      setReviewHistory(res.data.reviewHistory || []);
    } catch { setReviewHistory([]); } finally { setLoadingReview(false); }
  }, [unit._id]);

  const loadEvaluations = useCallback(async () => {
    setLoadingEval(true);
    try {
      const res = await getEvaluations(unit._id);
      setEvaluations(res.data.evaluations || []);
    } catch { setEvaluations([]); } finally { setLoadingEval(false); }
  }, [unit._id]);

  const loadReport = useCallback(async () => {
    setLoadingReport(true);
    setReportErr('');
    try {
      const res = await getUnitReport(unit._id);
      setReport(res.data);
    } catch (err) {
      setReportErr(err?.response?.data?.message || 'Failed to generate report');
    } finally {
      setLoadingReport(false);
    }
  }, [unit._id]);

  const handleOpenReport = () => {
    setShowReport(true);
    if (!report) loadReport();
  };

  const handleCopyMarkdown = () => {
    if (!report) return;
    navigator.clipboard.writeText(buildMarkdown(report)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownloadMarkdown = () => {
    if (!report) return;
    const md   = buildMarkdown(report);
    const blob = new Blob([md], { type: 'text/markdown' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    const name = (report.unit.name || 'report').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    a.href     = url;
    a.download = `${name}-report.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 2000);
  };

  const handleDownloadCommunity = () => {
    if (!report) return;
    const md   = buildCommunityMarkdown(report);
    const blob = new Blob([md], { type: 'text/markdown' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    const name = (report.unit.name || 'report').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    a.href     = url;
    a.download = `${name}-community-summary.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setCommunityDownloaded(true);
    setTimeout(() => setCommunityDownloaded(false), 2000);
  };

  const handleRefreshReport = () => {
    if (loadingReport) return;
    setReport(null);
    loadReport();
  };

  const handleTabChange = (tab) => {
    setDetailTab(tab);
    if (tab === 'notes'      && notes       === null) loadNotes();
    if (tab === 'review'     && reviewHistory === null) loadReview();
    if (tab === 'evaluation' && evaluations  === null) loadEvaluations();
  };

  const handleSetReview = async (e) => {
    e.preventDefault();
    if (!reviewTarget) return;
    setSavingReview(true);
    setReviewErr('');
    try {
      const res = await setReviewStatus(unit._id, { toStatus: reviewTarget, comment: reviewComment.trim() });
      setReviewStatus_(res.data.reviewStatus);
      setReviewHistory(res.data.reviewHistory || []);
      setReviewComment('');
      setReviewTarget('');
    } catch (err) {
      setReviewErr(err?.response?.data?.message || 'Failed to update status');
    } finally {
      setSavingReview(false);
    }
  };

  const handleObsAdded = () => { setShowAddObs(false); loadSummary(); };

  const handleAddEval = async (e) => {
    e.preventDefault();
    if (!evalForm.recommendation || !evalForm.summary.trim()) {
      setEvalErr('Recommendation and summary are required');
      return;
    }
    setSavingEval(true);
    setEvalErr('');
    try {
      const res = await addEvaluation(unit._id, evalForm);
      setEvaluations(prev => [res.data.evaluation, ...(prev || [])]);
      setShowAddEval(false);
      setEvalForm({ recommendation: '', summary: '', citedFactors: [], suggestedActions: '', confidence: 'medium' });
    } catch (err) {
      setEvalErr(err?.response?.data?.message || 'Failed to save evaluation');
    } finally {
      setSavingEval(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSavingNote(true);
    setNoteErr('');
    try {
      const res = await addNote(unit._id, { text: noteText.trim(), visibility: noteVis });
      setNotes(res.data.notes || []);
      setNoteText('');
    } catch (err) {
      setNoteErr(err?.response?.data?.message || 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const icon       = UNIT_TYPE_ICON[unit.unitType] || '📍';
  const scopeColor = SCOPE_COLOR[unit.scope] || '#94a3b8';
  const geomStr    = unit.geometry?.type || '?';

  const tabStyle = (active) => ({
    background: active ? '#1e3a5f' : 'transparent',
    border: `1px solid ${active ? '#3b82f6' : '#334155'}`,
    color: active ? '#7dd3fc' : '#64748b',
    borderRadius: 5, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  });

  // Factors shown in breakdown tab (filtered when chip selected)
  const visibleSummary = factorFilter
    ? (summary || []).filter(s => s._id === factorFilter)
    : (summary || []);

  return (
    <div style={{ background:'#1e293b', borderRadius:10, padding:20, flex:1, minWidth: 0 }}>
      {/* Header */}
      <div style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom:14 }}>
        <span style={{ fontSize:28 }}>{icon}</span>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <span style={{ color:'#f1f5f9', fontWeight:700, fontSize:16 }}>{unit.name}</span>
            {unit.isSensitive && (
              <span title="Sensitive location — coordinate precision is reduced for non-managers"
                style={{ fontSize:10, color:'#f59e0b', background:'rgba(245,158,11,0.12)',
                  border:'1px solid rgba(245,158,11,0.3)', borderRadius:4, padding:'1px 6px' }}>
                🔒 Sensitive
              </span>
            )}
            {/* Review status badge */}
            {(() => {
              const rs = REVIEW_STATUSES.find(r => r.value === reviewStatus);
              return rs ? (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: rs.color,
                  background: rs.color + '22',
                  border: `1px solid ${rs.color}44`,
                  borderRadius: 4, padding: '1px 8px',
                }}>{rs.label}</span>
              ) : null;
            })()}
            {/* Latest recommendation badge (shown once evaluations loaded) */}
            {evaluations && evaluations.length > 0 && (() => {
              const et = EVAL_TYPES.find(e => e.value === evaluations[0].recommendation);
              return et ? (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: et.color,
                  background: et.color + '22',
                  border: `1px solid ${et.color}44`,
                  borderRadius: 4, padding: '1px 8px',
                }}>{et.label}</span>
              ) : null;
            })()}
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:4 }}>
            <span style={{ fontSize:11, color:'#64748b' }}>
              {UNIT_TYPES.find(t => t.value === unit.unitType)?.label || unit.unitType}
            </span>
            <span style={{ fontSize:11, color: scopeColor }}>● {unit.scope}</span>
            <span style={{ fontSize:11, color:'#64748b' }}>{geomStr}</span>
            {unit.areaHa != null && <span style={{ fontSize:11, color:'#64748b' }}>{unit.areaHa.toLocaleString()} ha</span>}
          </div>
          {unit.geometryIsMasked && (
            <div style={{ fontSize:10, color:'#f59e0b', marginTop:4 }}>
              ⚠ Map location is approximate (sensitive unit)
            </div>
          )}
        </div>
      </div>

      {unit.description && (
        <div style={{ fontSize:13, color:'#94a3b8', marginBottom:14, lineHeight:1.5 }}>
          {unit.description}
        </div>
      )}

      {unit.tags?.length > 0 && (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
          {unit.tags.map(t => (
            <span key={t} style={{ background:'#0f172a', color:'#64748b', fontSize:11,
              borderRadius:4, padding:'2px 8px', border:'1px solid #1e293b' }}>{t}</span>
          ))}
        </div>
      )}

      {/* Tab bar */}
      <div style={{ borderTop:'1px solid #0f172a', paddingTop:14, marginBottom:12 }}>
        <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
          <button style={tabStyle(detailTab === 'factors')}
            onClick={() => handleTabChange('factors')}>Factor Breakdown</button>
          <button style={tabStyle(detailTab === 'snapshot')}
            onClick={() => handleTabChange('snapshot')}>Score Snapshot</button>
          <button style={tabStyle(detailTab === 'notes')}
            onClick={() => handleTabChange('notes')}>📋 Staff Notes</button>
          {isManager && (
            <button style={tabStyle(detailTab === 'review')}
              onClick={() => handleTabChange('review')}>🔄 Review</button>
          )}
          {isManager && (
            <button style={tabStyle(detailTab === 'evaluation')}
              onClick={() => handleTabChange('evaluation')}>✅ Evaluation</button>
          )}
          {isManager && (
            <button
              onClick={showReport ? () => setShowReport(false) : handleOpenReport}
              style={{
                ...tabStyle(showReport),
                marginLeft: detailTab !== 'factors' || showAddObs ? 'auto' : undefined,
              }}
            >📄 Report</button>
          )}
          {isManager && detailTab === 'factors' && !showAddObs && !showReport && (
            <button onClick={() => setShowAddObs(true)} style={{
              background:'#1e3a5f', border:'1px solid #3b82f6', borderRadius:5,
              color:'#7dd3fc', padding:'5px 12px', cursor:'pointer', fontSize:11,
              fontWeight:600, marginLeft:'auto',
            }}>
              + Observation
            </button>
          )}
        </div>
      </div>

      {/* Report panel — replaces tab content when active */}
      {showReport && (
        <div id="report-print-area">
          <style>{`@media print { body * { visibility: hidden } #report-print-area, #report-print-area * { visibility: visible } #report-print-area { position: fixed; left: 0; top: 0; width: 100%; background: #0b1120; padding: 24px; color: #e2e8f0 } }`}</style>
          {/* Report toolbar */}
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:16, flexWrap:'wrap' }}>
            <div>
              <span style={{ fontSize:9, fontWeight:700, color:'#334155', letterSpacing:0.8, display:'block', marginBottom:1 }}>
                UNIT REPORT
              </span>
              <span style={{ fontSize:14, fontWeight:700, color:'#e2e8f0' }}>{unit.name}</span>
            </div>
            <div style={{ marginLeft:'auto', display:'flex', gap:6, flexWrap:'wrap' }}>
              <button
                onClick={handleCopyMarkdown}
                disabled={!report}
                style={{
                  background: copied ? '#14532d' : '#1e293b',
                  border: `1px solid ${copied ? '#22c55e' : '#334155'}`,
                  borderRadius:5, color: copied ? '#86efac' : '#94a3b8',
                  padding:'5px 12px', cursor: report ? 'pointer' : 'not-allowed',
                  fontSize:11, fontWeight:600,
                }}
              >{copied ? '✓ Copied!' : '📋 Copy Markdown'}</button>
              <button
                onClick={handleDownloadMarkdown}
                disabled={!report}
                style={{
                  background: downloaded ? '#14532d' : '#1e293b',
                  border: `1px solid ${downloaded ? '#22c55e' : '#334155'}`,
                  borderRadius:5, color: downloaded ? '#86efac' : '#94a3b8',
                  padding:'5px 12px', cursor: report ? 'pointer' : 'not-allowed',
                  fontSize:11, fontWeight:600,
                }}
              >{downloaded ? '✓ Downloaded!' : '⬇ Download .md'}</button>
              <button
                onClick={handleDownloadCommunity}
                disabled={!report}
                style={{
                  background: communityDownloaded ? '#14532d' : '#1e293b',
                  border: `1px solid ${communityDownloaded ? '#22c55e' : '#0ea5e9'}`,
                  borderRadius:5, color: communityDownloaded ? '#86efac' : '#7dd3fc',
                  padding:'5px 12px', cursor: report ? 'pointer' : 'not-allowed',
                  fontSize:11, fontWeight:600,
                }}
                title="Download a simplified, TK-masked community-safe version"
              >{communityDownloaded ? '✓ Exported!' : '🌐 Community Export'}</button>
              <button
                onClick={handleRefreshReport}
                disabled={loadingReport}
                style={{
                  background:'#1e293b', border:'1px solid #334155', borderRadius:5,
                  color: loadingReport ? '#334155' : '#94a3b8', padding:'5px 12px',
                  cursor: loadingReport ? 'not-allowed' : 'pointer', fontSize:11, fontWeight:600,
                }}
              >↻ Refresh</button>
              <button
                onClick={() => window.print()}
                disabled={!report}
                style={{
                  background:'#1e293b', border:'1px solid #334155', borderRadius:5,
                  color:'#94a3b8', padding:'5px 12px',
                  cursor: report ? 'pointer' : 'not-allowed', fontSize:11, fontWeight:600,
                }}
              >🖨 Print</button>
              <button
                onClick={() => setShowReport(false)}
                style={{
                  background:'none', border:'1px solid #334155', borderRadius:5,
                  color:'#64748b', padding:'5px 12px', cursor:'pointer', fontSize:11,
                }}
              >✕ Close</button>
            </div>
          </div>

          {loadingReport && (
            <div style={{ color:'#475569', fontSize:13, padding:'24px 0', textAlign:'center' }}>
              Generating report…
            </div>
          )}
          {reportErr && (
            <div style={{ color:'#f87171', fontSize:12, padding:'12px 0' }}>{reportErr}</div>
          )}

          {!loadingReport && report && (() => {
            const { unit: ru, scoreSnapshot: ss, evaluations: evs, generatedAt, generatedBy } = report;
            const fmt = (d) => d ? new Date(d).toLocaleDateString() : '—';
            const pct = (v) => `${Math.round((v ?? 0) * 100)}%`;
            const sectionHead = (label) => (
              <div style={{ fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1,
                marginBottom:8, marginTop:18, paddingBottom:4, borderBottom:'1px solid #0f172a' }}>
                {label}
              </div>
            );
            return (
              <div style={{ fontSize:13, color:'#e2e8f0', lineHeight:1.6 }}>
                {/* Generated-by line */}
                <div style={{ fontSize:10, color:'#334155', marginBottom:12 }}>
                  Generated {new Date(generatedAt).toLocaleString()} · {generatedBy}
                </div>

                {/* Unit profile */}
                {sectionHead('UNIT PROFILE')}
                <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:8 }}>
                  <span style={{ fontSize:12, color:'#94a3b8' }}>
                    {UNIT_TYPES.find(t => t.value === ru.unitType)?.label || ru.unitType}
                  </span>
                  <span style={{ fontSize:12, color: SCOPE_COLOR[ru.scope] || '#94a3b8' }}>● {ru.scope}</span>
                  {ru.areaHa && <span style={{ fontSize:12, color:'#94a3b8' }}>{ru.areaHa.toLocaleString()} ha</span>}
                  {ru.isSensitive && (
                    <span style={{ fontSize:10, color:'#f59e0b', background:'rgba(245,158,11,0.1)',
                      border:'1px solid rgba(245,158,11,0.3)', borderRadius:4, padding:'1px 6px' }}>
                      🔒 Sensitive
                    </span>
                  )}
                  {(() => {
                    const rs = REVIEW_STATUSES.find(r => r.value === ru.reviewStatus);
                    return rs && rs.value !== 'open' ? (
                      <span style={{ fontSize:10, fontWeight:700, color:rs.color,
                        background:rs.color+'22', border:`1px solid ${rs.color}44`,
                        borderRadius:4, padding:'1px 8px' }}>{rs.label}</span>
                    ) : null;
                  })()}
                </div>
                {ru.description && (
                  <div style={{ fontSize:12, color:'#94a3b8', marginBottom:8, lineHeight:1.5 }}>
                    {ru.description}
                  </div>
                )}
                {ru.tags?.length > 0 && (
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                    {ru.tags.map(t => (
                      <span key={t} style={{ background:'#0f172a', color:'#64748b', fontSize:11,
                        borderRadius:4, padding:'2px 8px', border:'1px solid #1e293b' }}>{t}</span>
                    ))}
                  </div>
                )}
                {ru.externalId && (
                  <div style={{ fontSize:11, color:'#334155' }}>External ID: {ru.externalId}</div>
                )}

                {/* Score snapshot */}
                {ss && (
                  <>
                    {sectionHead('SCORE SNAPSHOT')}
                    <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:10 }}>
                      <span style={{
                        fontSize:26, fontWeight:800,
                        color: ss.weightedScore >= 0.7 ? '#22c55e' : ss.weightedScore >= 0.4 ? '#f59e0b' : '#ef4444',
                      }}>{pct(ss.weightedScore)}</span>
                      <span style={{ fontSize:11, color: CONF_COLOR[ss.confidence] || '#94a3b8' }}>
                        {ss.confidence} confidence · equal weights
                      </span>
                    </div>
                    <div style={{ background:'#0f172a', borderRadius:6, overflow:'hidden' }}>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 56px 70px 70px 70px',
                        padding:'6px 10px', fontSize:9, color:'#334155', fontWeight:700,
                        letterSpacing:0.5, gap:8 }}>
                        <span>FACTOR</span><span style={{textAlign:'right'}}>LATEST</span>
                        <span style={{textAlign:'right'}}>OBS</span>
                        <span style={{textAlign:'right'}}>CONF</span>
                        <span style={{textAlign:'right'}}>CONTRIB</span>
                      </div>
                      {ss.factorInputs.map((f, i) => {
                        const label = FACTOR_TYPES.find(ft => ft.value === f.factor)?.label || f.factor;
                        const col   = FACTOR_COLOR[f.factor] || '#94a3b8';
                        return (
                          <div key={f.factor} style={{
                            display:'grid', gridTemplateColumns:'1fr 56px 70px 70px 70px',
                            padding:'6px 10px', gap:8, alignItems:'center',
                            background: i % 2 === 1 ? '#0a1628' : 'transparent',
                          }}>
                            <span style={{ fontSize:12, color:col }}>{label}</span>
                            <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
                              <span style={{ fontSize:12, color:'#e2e8f0', fontWeight:600 }}>
                                {pct(f.latestValue)}
                              </span>
                              <div style={{ width:44, height:3, background:'#1e293b', borderRadius:2, marginTop:2 }}>
                                <div style={{ width:`${Math.round((f.latestValue ?? 0) * 100)}%`, height:3, background:col, borderRadius:2 }} />
                              </div>
                            </div>
                            <span style={{ fontSize:11, color:'#64748b', textAlign:'right' }}>{f.count}</span>
                            <span style={{ fontSize:11, color: CONF_COLOR[f.confidence] || '#94a3b8', textAlign:'right' }}>
                              {f.confidence}
                            </span>
                            <span style={{ fontSize:11, color:'#475569', textAlign:'right' }}>
                              +{pct(f.contribution)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {ss.factorInputs[0]?.rationale && ss.factorInputs.some(f => f.rationale) && (
                      <div style={{ marginTop:8 }}>
                        {ss.factorInputs.filter(f => f.rationale).map(f => (
                          <div key={f.factor} style={{ fontSize:11, color:'#64748b', marginBottom:3 }}>
                            <span style={{ color: FACTOR_COLOR[f.factor] || '#94a3b8', fontWeight:600 }}>
                              {FACTOR_TYPES.find(ft => ft.value === f.factor)?.label || f.factor}:
                            </span>{' '}{f.rationale}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Evaluations */}
                {evs?.length > 0 && (
                  <>
                    {sectionHead(`STAFF EVALUATIONS (${evs.length})`)}
                    {evs.map((ev, i) => {
                      const et  = EVAL_TYPES.find(e => e.value === ev.recommendation);
                      const col = et?.color || '#94a3b8';
                      return (
                        <div key={ev._id || i} style={{
                          background:'#0f172a', borderRadius:6, padding:'10px 12px',
                          marginBottom:8, borderLeft:`3px solid ${col}`,
                        }}>
                          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:4 }}>
                            {i === 0 && (
                              <span style={{ fontSize:9, background:'#1e293b', color:'#64748b',
                                borderRadius:3, padding:'1px 5px' }}>LATEST</span>
                            )}
                            <span style={{ fontSize:12, fontWeight:700, color:col }}>
                              {et?.label || ev.recommendation}
                            </span>
                            <span style={{ fontSize:10, color:'#475569' }}>· {ev.confidence} confidence</span>
                            {ev.linkedReviewStatus && ev.linkedReviewStatus !== 'open' && (
                              <span style={{
                                fontSize:9, color: REVIEW_COLOR[ev.linkedReviewStatus] || '#64748b',
                                background:(REVIEW_COLOR[ev.linkedReviewStatus] || '#334155')+'22',
                                borderRadius:3, padding:'1px 5px',
                              }}>
                                {REVIEW_STATUSES.find(r => r.value === ev.linkedReviewStatus)?.label || ev.linkedReviewStatus}
                              </span>
                            )}
                            <span style={{ fontSize:10, color:'#334155', marginLeft:'auto' }}>
                              {ev.reviewedByName} · {fmt(ev.reviewedAt)}
                            </span>
                          </div>
                          <div style={{ fontSize:12, color:'#94a3b8', lineHeight:1.5, marginBottom:4 }}>
                            {ev.summary}
                          </div>
                          {ev.suggestedActions && (
                            <div style={{ fontSize:11, color:'#64748b', fontStyle:'italic' }}>
                              ↳ {ev.suggestedActions}
                            </div>
                          )}
                          {ev.citedFactors?.length > 0 && (
                            <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:5 }}>
                              {ev.citedFactors.map(f => {
                                const label = FACTOR_TYPES.find(ft => ft.value === f)?.label || f;
                                const c = FACTOR_COLOR[f] || '#94a3b8';
                                return (
                                  <span key={f} style={{ fontSize:9, color:c, background:c+'22',
                                    borderRadius:3, padding:'1px 5px' }}>{label}</span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
                {!evs?.length && (
                  <>
                    {sectionHead('STAFF EVALUATIONS')}
                    <div style={{ fontSize:12, color:'#334155', padding:'10px 0', fontStyle:'italic' }}>
                      No staff evaluations recorded yet.
                    </div>
                  </>
                )}

                {/* Review history */}
                {ru.reviewHistory?.length > 0 && (
                  <>
                    {sectionHead(`REVIEW HISTORY (${ru.reviewHistory.length})`)}
                    {ru.reviewHistory.map((h, i) => {
                      const toRs   = REVIEW_STATUSES.find(r => r.value === h.toStatus);
                      const fromRs = REVIEW_STATUSES.find(r => r.value === h.fromStatus);
                      return (
                        <div key={i} style={{
                          background:'#0f172a', borderRadius:5, padding:'7px 10px',
                          marginBottom:5, borderLeft:`3px solid ${toRs?.color || '#334155'}`,
                        }}>
                          <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                            <span style={{ fontSize:11, color:fromRs?.color || '#475569' }}>
                              {fromRs?.label || h.fromStatus}
                            </span>
                            <span style={{ fontSize:10, color:'#334155' }}>→</span>
                            <span style={{ fontSize:11, fontWeight:700, color:toRs?.color || '#e2e8f0' }}>
                              {toRs?.label || h.toStatus}
                            </span>
                            <span style={{ fontSize:10, color:'#334155', marginLeft:'auto' }}>
                              {h.changedByName || 'Staff'} · {fmt(h.changedAt)}
                            </span>
                          </div>
                          {h.comment && (
                            <div style={{ fontSize:11, color:'#64748b', marginTop:3 }}>{h.comment}</div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
                {!ru.reviewHistory?.length && (
                  <>
                    {sectionHead('REVIEW HISTORY')}
                    <div style={{ fontSize:12, color:'#334155', padding:'10px 0', fontStyle:'italic' }}>
                      No review transitions on record.
                    </div>
                  </>
                )}

                {/* Notes */}
                {ru.notes?.length > 0 && (
                  <>
                    {sectionHead(`STAFF NOTES (${ru.notes.length})`)}
                    {ru.notes.map((n, i) => (
                      <div key={n._id || i} style={{
                        background:'#0f172a', borderRadius:5, padding:'7px 10px', marginBottom:5,
                        borderLeft:`3px solid ${n.visibility === 'community' ? '#0ea5e9' : '#1e3a5f'}`,
                      }}>
                        <div style={{ fontSize:12, color:'#cbd5e1', lineHeight:1.5, whiteSpace:'pre-wrap' }}>
                          {n.text}
                        </div>
                        <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:4 }}>
                          <span style={{ fontSize:9, color: n.visibility === 'community' ? '#0ea5e9' : '#475569' }}>
                            {n.visibility === 'community' ? '🌐 COMMUNITY' : '🔒 INTERNAL'}
                          </span>
                          <span style={{ fontSize:10, color:'#334155' }}>
                            {n.authorName || 'Staff'} · {fmt(n.createdAt)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {!ru.notes?.length && (
                  <>
                    {sectionHead('STAFF NOTES')}
                    <div style={{ fontSize:12, color:'#334155', padding:'10px 0', fontStyle:'italic' }}>
                      No staff notes recorded.
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Tab content — hidden when report panel is open */}
      {!showReport && (
        <>

      {detailTab === 'factors' && (
        <>
          {showAddObs && (
            <AddObservationForm
              unit={unit}
              tenantId={tenantId}
              onAdded={handleObsAdded}
              onCancel={() => setShowAddObs(false)}
            />
          )}
          {/* Factor filter chips */}
          {!loadingFac && (summary || []).length > 1 && (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
              <button
                onClick={() => setFactorFilter('')}
                style={{
                  background: factorFilter === '' ? '#1e3a5f' : '#0f172a',
                  border: `1px solid ${factorFilter === '' ? '#3b82f6' : '#1e293b'}`,
                  color: factorFilter === '' ? '#7dd3fc' : '#475569',
                  borderRadius: 12, padding:'3px 10px', cursor:'pointer', fontSize:10,
                }}>All</button>
              {(summary || []).map(s => {
                const label = FACTOR_TYPES.find(f => f.value === s._id)?.label || s._id;
                const color = FACTOR_COLOR[s._id] || '#94a3b8';
                const active = factorFilter === s._id;
                return (
                  <button key={s._id} onClick={() => setFactorFilter(active ? '' : s._id)} style={{
                    background: active ? color + '22' : '#0f172a',
                    border: `1px solid ${active ? color : '#1e293b'}`,
                    color: active ? color : '#475569',
                    borderRadius: 12, padding:'3px 10px', cursor:'pointer', fontSize:10,
                  }}>{label}</button>
                );
              })}
            </div>
          )}
          {loadingFac ? (
            <div style={{ color:'#475569', fontSize:12 }}>Loading…</div>
          ) : visibleSummary.length === 0 ? (
            <div style={{ color:'#475569', fontSize:13, padding:'20px 0', textAlign:'center' }}>
              {(summary || []).length === 0 ? (
                <>
                  No factor observations recorded yet.
                  {isManager && !showAddObs && (
                    <div style={{ marginTop:8, fontSize:12 }}>
                      Use "+ Observation" to record the first factor reading.
                    </div>
                  )}
                </>
              ) : 'No observations for selected factor.'}
            </div>
          ) : (
            visibleSummary.map(s => (
              <FactorBar
                key={s._id}
                factor={s._id}
                avgValue={s.avgValue}
                latestValue={s.latestValue}
                count={s.count}
                confidence={s.latestConfidence}
              />
            ))
          )}
        </>
      )}

      {/* Score snapshot tab */}
      {detailTab === 'snapshot' && (
        <ScoreSnapshotPanel unit={unit} tenantId={tenantId} isManager={isManager} />
      )}

      {/* Review workflow tab */}
      {detailTab === 'review' && isManager && (
        <div>
          {/* Current status + transition form */}
          <div style={{ background: '#0f172a', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#475569', marginBottom: 6 }}>CURRENT STATUS</div>
            {(() => {
              const rs = REVIEW_STATUSES.find(r => r.value === reviewStatus);
              return rs ? (
                <span style={{
                  fontSize: 13, fontWeight: 700, color: rs.color,
                  background: rs.color + '22', borderRadius: 5, padding: '4px 12px',
                }}>{rs.label}</span>
              ) : null;
            })()}
          </div>

          <form onSubmit={handleSetReview} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#475569', marginBottom: 6 }}>TRANSITION TO</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {REVIEW_STATUSES.filter(r => r.value !== reviewStatus).map(r => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setReviewTarget(r.value)}
                  style={{
                    background: reviewTarget === r.value ? r.color + '22' : '#0f172a',
                    border: `1px solid ${reviewTarget === r.value ? r.color : '#334155'}`,
                    color: reviewTarget === r.value ? r.color : '#64748b',
                    borderRadius: 5, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  }}
                >{r.label}</button>
              ))}
            </div>
            <textarea
              rows={2}
              placeholder="Comment (required for Escalated, optional otherwise)…"
              value={reviewComment}
              onChange={e => setReviewComment(e.target.value)}
              maxLength={1000}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: '#0f172a', border: '1px solid #334155',
                borderRadius: 6, color: '#e2e8f0', padding: '8px 10px',
                fontSize: 12, resize: 'vertical', marginBottom: 8,
              }}
            />
            {reviewErr && <div style={{ fontSize: 11, color: '#f87171', marginBottom: 6 }}>{reviewErr}</div>}
            <button
              type="submit"
              disabled={savingReview || !reviewTarget}
              style={{
                background: savingReview || !reviewTarget ? '#1e293b' : '#1d4ed8',
                border: 'none', borderRadius: 6, color: '#fff',
                padding: '6px 18px', cursor: savingReview || !reviewTarget ? 'not-allowed' : 'pointer',
                fontSize: 12, fontWeight: 600,
              }}
            >{savingReview ? 'Saving…' : 'Update Status'}</button>
          </form>

          {/* Review history timeline */}
          <div style={{ fontSize: 11, color: '#475569', marginBottom: 8 }}>HISTORY</div>
          {loadingReview && <div style={{ fontSize: 11, color: '#475569' }}>Loading…</div>}
          {!loadingReview && reviewHistory !== null && reviewHistory.length === 0 && (
            <div style={{ fontSize: 12, color: '#334155' }}>No review transitions yet.</div>
          )}
          {!loadingReview && reviewHistory && reviewHistory.map((h, i) => {
            const toRs   = REVIEW_STATUSES.find(r => r.value === h.toStatus);
            const fromRs = REVIEW_STATUSES.find(r => r.value === h.fromStatus);
            return (
              <div key={i} style={{
                background: '#0f172a', borderRadius: 6, padding: '8px 12px', marginBottom: 6,
                borderLeft: `3px solid ${toRs?.color || '#334155'}`,
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: fromRs?.color || '#475569' }}>
                    {fromRs?.label || h.fromStatus}
                  </span>
                  <span style={{ fontSize: 10, color: '#334155' }}>→</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: toRs?.color || '#e2e8f0' }}>
                    {toRs?.label || h.toStatus}
                  </span>
                  <span style={{ fontSize: 10, color: '#334155', marginLeft: 'auto' }}>
                    {h.changedByName || 'Staff'} · {h.changedAt ? new Date(h.changedAt).toLocaleString() : '—'}
                  </span>
                </div>
                {h.comment && (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{h.comment}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Evaluation / Recommendation tab */}
      {detailTab === 'evaluation' && isManager && (
        <div>
          {/* Latest recommendation summary */}
          {evaluations && evaluations.length > 0 && (() => {
            const latest = evaluations[0];
            const et = EVAL_TYPES.find(e => e.value === latest.recommendation);
            return (
              <div style={{
                background: (et?.color || '#64748b') + '18',
                border: `1px solid ${et?.color || '#334155'}44`,
                borderRadius: 8, padding: '12px 14px', marginBottom: 14,
              }}>
                <div style={{ fontSize: 10, color: '#475569', marginBottom: 4 }}>CURRENT RECOMMENDATION</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: et?.color || '#e2e8f0' }}>
                    {et?.label || latest.recommendation}
                  </span>
                  <span style={{ fontSize: 10, color: '#475569' }}>· {latest.confidence} confidence</span>
                  <span style={{ fontSize: 10, color: '#334155', marginLeft: 'auto' }}>
                    {latest.reviewedByName} · {latest.reviewedAt ? new Date(latest.reviewedAt).toLocaleDateString() : '—'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6, lineHeight: 1.5 }}>
                  {latest.summary}
                </div>
              </div>
            );
          })()}

          {/* New evaluation form toggle */}
          {!showAddEval ? (
            <button
              onClick={() => setShowAddEval(true)}
              style={{
                background: '#1e3a5f', border: '1px solid #3b82f6', borderRadius: 5,
                color: '#7dd3fc', padding: '6px 14px', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, marginBottom: 14,
              }}
            >+ New Evaluation</button>
          ) : (
            <form onSubmit={handleAddEval} style={{
              background: '#0f2240', borderRadius: 8, padding: '14px 16px', marginBottom: 16,
            }}>
              <div style={{ fontSize: 11, color: '#475569', marginBottom: 8 }}>NEW EVALUATION</div>

              {/* Recommendation type */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 5 }}>RECOMMENDATION</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {EVAL_TYPES.map(et => (
                    <button
                      key={et.value}
                      type="button"
                      onClick={() => setEvalForm(f => ({ ...f, recommendation: et.value }))}
                      style={{
                        background: evalForm.recommendation === et.value ? et.color + '22' : '#0a1628',
                        border: `1px solid ${evalForm.recommendation === et.value ? et.color : '#334155'}`,
                        color: evalForm.recommendation === et.value ? et.color : '#64748b',
                        borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                      }}
                    >{et.label}</button>
                  ))}
                </div>
              </div>

              {/* Confidence */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>CONFIDENCE</span>
                {['low', 'medium', 'high'].map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setEvalForm(f => ({ ...f, confidence: c }))}
                    style={{
                      background: evalForm.confidence === c ? '#1e3a5f' : '#0a1628',
                      border: `1px solid ${evalForm.confidence === c ? '#3b82f6' : '#334155'}`,
                      color: evalForm.confidence === c ? '#7dd3fc' : '#475569',
                      borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 11,
                    }}
                  >{c}</button>
                ))}
              </div>

              {/* Cited factors */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 5 }}>CITED FACTORS (select all that apply)</div>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {FACTOR_TYPES.map(ft => {
                    const active = evalForm.citedFactors.includes(ft.value);
                    const col = FACTOR_COLOR[ft.value] || '#94a3b8';
                    return (
                      <button
                        key={ft.value}
                        type="button"
                        onClick={() => setEvalForm(f => ({
                          ...f,
                          citedFactors: active
                            ? f.citedFactors.filter(x => x !== ft.value)
                            : [...f.citedFactors, ft.value],
                        }))}
                        style={{
                          background: active ? col + '22' : '#0a1628',
                          border: `1px solid ${active ? col : '#1e293b'}`,
                          color: active ? col : '#475569',
                          borderRadius: 12, padding: '3px 8px', cursor: 'pointer', fontSize: 10,
                        }}
                      >{ft.label}</button>
                    );
                  })}
                </div>
              </div>

              {/* Summary */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>SUMMARY (required)</div>
                <textarea
                  rows={3}
                  required
                  placeholder="Summarise the basis for this recommendation…"
                  value={evalForm.summary}
                  onChange={e => setEvalForm(f => ({ ...f, summary: e.target.value }))}
                  maxLength={2000}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: '#0a1628', border: '1px solid #334155',
                    borderRadius: 6, color: '#e2e8f0', padding: '8px 10px',
                    fontSize: 12, resize: 'vertical',
                  }}
                />
              </div>

              {/* Suggested actions */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>SUGGESTED ACTIONS (optional)</div>
                <textarea
                  rows={2}
                  placeholder="Specific next steps or conditions…"
                  value={evalForm.suggestedActions}
                  onChange={e => setEvalForm(f => ({ ...f, suggestedActions: e.target.value }))}
                  maxLength={1000}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: '#0a1628', border: '1px solid #334155',
                    borderRadius: 6, color: '#e2e8f0', padding: '8px 10px',
                    fontSize: 12, resize: 'vertical',
                  }}
                />
              </div>

              {evalErr && <div style={{ fontSize: 11, color: '#f87171', marginBottom: 6 }}>{evalErr}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="submit"
                  disabled={savingEval || !evalForm.recommendation || !evalForm.summary.trim()}
                  style={{
                    background: savingEval || !evalForm.recommendation || !evalForm.summary.trim()
                      ? '#1e293b' : '#1d4ed8',
                    border: 'none', borderRadius: 6, color: '#fff',
                    padding: '6px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  }}
                >{savingEval ? 'Saving…' : 'Submit Evaluation'}</button>
                <button
                  type="button"
                  onClick={() => { setShowAddEval(false); setEvalErr(''); }}
                  style={{
                    background: 'none', border: '1px solid #334155', borderRadius: 6,
                    color: '#64748b', padding: '6px 14px', cursor: 'pointer', fontSize: 12,
                  }}
                >Cancel</button>
              </div>
            </form>
          )}

          {/* Evaluation history */}
          <div style={{ fontSize: 11, color: '#475569', marginBottom: 8 }}>
            HISTORY ({evaluations?.length ?? '…'})
          </div>
          {loadingEval && <div style={{ fontSize: 11, color: '#475569' }}>Loading…</div>}
          {!loadingEval && evaluations !== null && evaluations.length === 0 && (
            <div style={{ fontSize: 12, color: '#334155' }}>No evaluations submitted yet.</div>
          )}
          {!loadingEval && evaluations && evaluations.map((ev, i) => {
            const et  = EVAL_TYPES.find(e => e.value === ev.recommendation);
            const col = et?.color || '#94a3b8';
            return (
              <div key={ev._id || i} style={{
                background: '#0f172a', borderRadius: 6, padding: '10px 12px', marginBottom: 8,
                borderLeft: `3px solid ${col}`,
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: col }}>
                    {et?.label || ev.recommendation}
                  </span>
                  <span style={{ fontSize: 10, color: '#475569' }}>· {ev.confidence} confidence</span>
                  {ev.linkedReviewStatus && ev.linkedReviewStatus !== 'open' && (
                    <span style={{
                      fontSize: 9, color: REVIEW_COLOR[ev.linkedReviewStatus] || '#64748b',
                      background: (REVIEW_COLOR[ev.linkedReviewStatus] || '#334155') + '22',
                      borderRadius: 3, padding: '1px 5px',
                    }}>
                      {REVIEW_STATUSES.find(r => r.value === ev.linkedReviewStatus)?.label || ev.linkedReviewStatus}
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: '#334155', marginLeft: 'auto' }}>
                    {ev.reviewedByName} · {ev.reviewedAt ? new Date(ev.reviewedAt).toLocaleDateString() : '—'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5, marginBottom: 4 }}>
                  {ev.summary}
                </div>
                {ev.citedFactors?.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                    {ev.citedFactors.map(f => {
                      const label = FACTOR_TYPES.find(ft => ft.value === f)?.label || f;
                      const c = FACTOR_COLOR[f] || '#94a3b8';
                      return (
                        <span key={f} style={{
                          fontSize: 9, color: c, background: c + '22',
                          borderRadius: 3, padding: '1px 5px',
                        }}>{label}</span>
                      );
                    })}
                  </div>
                )}
                {ev.suggestedActions && (
                  <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
                    ↳ {ev.suggestedActions}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Staff notes tab */}
      {detailTab === 'notes' && (
        <div>
          {/* Add note form (managers only) */}
          {isManager && (
            <form onSubmit={handleAddNote} style={{ marginBottom: 16 }}>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add an evaluation note…"
                maxLength={1000}
                rows={3}
                style={{
                  width: '100%', background: '#0f172a', border: '1px solid #334155',
                  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px',
                  fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
                }}
              />
              {/* Visibility toggle */}
              <div style={{ display: 'flex', gap: 6, marginTop: 6, marginBottom: 6 }}>
                {[
                  { val: 'internal',  label: '🔒 Internal only',      desc: 'Only managers & staff' },
                  { val: 'community', label: '🌐 Community visible',  desc: 'All members with access' },
                ].map(opt => (
                  <button
                    key={opt.val}
                    type="button"
                    title={opt.desc}
                    onClick={() => setNoteVis(opt.val)}
                    style={{
                      background: noteVis === opt.val ? '#1e3a5f' : '#0f172a',
                      border: `1px solid ${noteVis === opt.val ? '#3b82f6' : '#334155'}`,
                      color: noteVis === opt.val ? '#7dd3fc' : '#475569',
                      borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: 11,
                    }}
                  >{opt.label}</button>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginTop: 4 }}>
                {noteErr && <span style={{ fontSize: 11, color: '#f87171' }}>{noteErr}</span>}
                <span style={{ fontSize: 10, color: '#334155', marginLeft: 'auto', marginRight: 10 }}>
                  {noteText.length}/1000
                </span>
                <button
                  type="submit"
                  disabled={savingNote || !noteText.trim()}
                  style={{
                    background: savingNote || !noteText.trim() ? '#1e293b' : '#1d4ed8',
                    border: 'none', borderRadius: 6, color: '#fff',
                    padding: '6px 16px', cursor: savingNote || !noteText.trim() ? 'not-allowed' : 'pointer',
                    fontSize: 12, fontWeight: 600,
                  }}
                >
                  {savingNote ? 'Saving…' : 'Save Note'}
                </button>
              </div>
            </form>
          )}

          {/* Notes list */}
          {loadingNotes && <div style={{ color: '#475569', fontSize: 12 }}>Loading notes…</div>}
          {!loadingNotes && notes !== null && notes.length === 0 && (
            <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
              No evaluation notes yet.
              {isManager && <div style={{ fontSize: 12, marginTop: 6 }}>Add the first note above.</div>}
            </div>
          )}
          {!loadingNotes && notes && notes.map((n, i) => (
            <div key={n._id || i} style={{
              background: '#0f172a', borderRadius: 6, padding: '10px 12px',
              marginBottom: 8, borderLeft: `3px solid ${n.visibility === 'community' ? '#0ea5e9' : '#1e3a5f'}`,
            }}>
              <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {n.text}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <span style={{ fontSize: 10, color: '#334155' }}>
                  {n.authorName || 'Staff'} · {n.createdAt ? new Date(n.createdAt).toLocaleString() : '—'}
                </span>
                <span title={n.visibility === 'community' ? 'Visible to all members with access' : 'Visible to managers and staff only'}
                  style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                    color: n.visibility === 'community' ? '#0ea5e9' : '#475569',
                  }}>
                  {n.visibility === 'community' ? '🌐 COMMUNITY' : '🔒 INTERNAL'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

        </>
      )} {/* end !showReport */}
    </div>
  );
}

// ─── Leadership markdown export ───────────────────────────────────────────────
function buildLeadershipMarkdown(displayUnits, allGroups, tenantFilter, isPlatformStaff, dateFrom, dateTo) {
  const now     = new Date();
  const dateStr = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const scopeLabel = (isPlatformStaff && tenantFilter !== 'all' && allGroups.length)
    ? (allGroups.find(([tid]) => tid === tenantFilter)?.[1]?.name || tenantFilter)
    : `All Communities (${(isPlatformStaff ? allGroups : []).length || 1} tenant${(isPlatformStaff ? allGroups : []).length !== 1 ? 's' : ''})`;
  const dateRangeLabel = (dateFrom || dateTo)
    ? `${dateFrom || 'beginning'} → ${dateTo || 'present'}`
    : 'All dates';

  const total      = displayUnits.length;
  const assessed   = displayUnits.filter(u => u.latestRecommendation).length;
  const unassessed = total - assessed;
  const escalated  = displayUnits.filter(u => u.reviewStatus === 'escalated').length;
  const underReview = displayUnits.filter(u => u.reviewStatus === 'under_review').length;

  const lines = [];
  lines.push(`# CIP Land Portfolio — Leadership Summary`);
  lines.push('');
  lines.push(`**Generated:** ${dateStr} at ${timeStr}`);
  lines.push(`**Scope:** ${scopeLabel}`);
  lines.push(`**Date range:** ${dateRangeLabel}`);
  lines.push(`**Total Units in view:** ${total}`);
  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push('## Overall Portfolio');
  lines.push('');
  lines.push('| Metric | Count |');
  lines.push('|--------|-------|');
  lines.push(`| Total Units | ${total} |`);
  lines.push(`| Assessed (with evaluation) | ${assessed} |`);
  lines.push(`| Unassessed (no evaluation) | ${unassessed} |`);
  lines.push(`| Escalated | ${escalated} |`);
  lines.push(`| Under Review | ${underReview} |`);
  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push('## Recommendation Breakdown');
  lines.push('');
  lines.push('| Recommendation | Count | Share |');
  lines.push('|----------------|-------|-------|');
  EVAL_TYPES.forEach(et => {
    const count = displayUnits.filter(u => u.latestRecommendation === et.value).length;
    if (count > 0) {
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      lines.push(`| ${et.label} | ${count} | ${pct}% |`);
    }
  });
  if (unassessed > 0) {
    const pct = total > 0 ? Math.round((unassessed / total) * 100) : 0;
    lines.push(`| *(No evaluation)* | ${unassessed} | ${pct}% |`);
  }
  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push('## Review Status');
  lines.push('');
  lines.push('| Status | Count | Share |');
  lines.push('|--------|-------|-------|');
  REVIEW_STATUSES.forEach(rs => {
    const count = displayUnits.filter(u => (u.reviewStatus || 'open') === rs.value).length;
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    lines.push(`| ${rs.label} | ${count} | ${pct}% |`);
  });
  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push('## Unit Types');
  lines.push('');
  lines.push('| Type | Count |');
  lines.push('|------|-------|');
  UNIT_TYPES.forEach(t => {
    const count = displayUnits.filter(u => u.unitType === t.value).length;
    if (count > 0) lines.push(`| ${t.label} | ${count} |`);
  });
  lines.push('');

  // Community breakdown table (only when platform staff viewing all tenants)
  if (isPlatformStaff && tenantFilter === 'all' && allGroups.length >= 2) {
    lines.push('---');
    lines.push('');
    lines.push('## Community Breakdown');
    lines.push('');
    lines.push('| Community | Units | Assessed | Escalated | Top Recommendation |');
    lines.push('|-----------|-------|----------|-----------|--------------------|');
    allGroups.forEach(([, grp]) => {
      const gc   = grp.units.length;
      const ga   = grp.units.filter(u => u.latestRecommendation).length;
      const ge   = grp.units.filter(u => u.reviewStatus === 'escalated').length;
      const recMap = {};
      grp.units.filter(u => u.latestRecommendation).forEach(u => {
        recMap[u.latestRecommendation] = (recMap[u.latestRecommendation] || 0) + 1;
      });
      const topRec = Object.entries(recMap).sort((a, b) => b[1] - a[1])[0];
      const topEt  = topRec ? EVAL_TYPES.find(e => e.value === topRec[0]) : null;
      const topStr = topEt ? `${topEt.label} ×${topRec[1]}` : '—';
      lines.push(`| ${grp.name} | ${gc} | ${ga} | ${ge} | ${topStr} |`);
    });
    lines.push('');
  }

  // Units needing attention
  const attention = displayUnits.filter(u =>
    u.reviewStatus === 'escalated' || u.reviewStatus === 'under_review' || !u.latestRecommendation
  ).slice(0, 20);
  if (attention.length > 0) {
    lines.push('---');
    lines.push('');
    lines.push('## Units Needing Attention');
    lines.push('');
    attention.forEach(u => {
      const flag  = u.reviewStatus === 'escalated' ? '[ESCALATED]'
        : !u.latestRecommendation ? '[NO EVAL]' : '[UNDER REVIEW]';
      const rec   = u.latestRecommendation
        ? (EVAL_TYPES.find(e => e.value === u.latestRecommendation)?.label || u.latestRecommendation)
        : 'No evaluation';
      const rs    = REVIEW_STATUSES.find(r => r.value === (u.reviewStatus || 'open'))?.label || 'Open';
      lines.push(`- **${flag}** ${u.name} — ${rec} / ${rs}`);
    });
    lines.push('');
  }

  lines.push('---');
  lines.push('');
  lines.push('_CIP internal leadership export. Contact platform staff before distribution._');
  return lines.join('\n');
}

// ─── Leadership CSV export ────────────────────────────────────────────────────
function buildLeadershipCSV(displayUnits) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const headers = ['Name','Type','Recommendation','Review Status','Score (%)','Tenant','Created'];
  const rows = displayUnits.map(u => {
    const typeLabel = UNIT_TYPES.find(t => t.value === u.unitType)?.label || u.unitType || '';
    const evalLabel = u.latestRecommendation
      ? (EVAL_TYPES.find(e => e.value === u.latestRecommendation)?.label || u.latestRecommendation)
      : '';
    const rsLabel   = REVIEW_STATUSES.find(r => r.value === (u.reviewStatus || 'open'))?.label || 'Open';
    const score     = u.compositeScore != null ? Math.round(u.compositeScore * 100) : '';
    const tenant    = u.tenantId?.name || '';
    const created   = u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-GB') : '';
    return [esc(u.name), esc(typeLabel), esc(evalLabel), esc(rsLabel), score, esc(tenant), esc(created)].join(',');
  });
  return [headers.join(','), ...rows].join('\r\n');
}

// ─── Portfolio Dashboard ───────────────────────────────────────────────────────
function LandPortfolioDashboard({ units, isManager, isPlatformStaff, onDrillEval, onDrillReview, onSelectUnit,
  dateFrom, dateTo, tenantFilter,
  onDateFromChange, onDateToChange, onTenantFilterChange }) {
  // ── Tenant groups (built from ALL units, used for dropdown) ───────────────
  const allTenantGroupMap = {};
  units.forEach(u => {
    const tid   = u.tenantId?._id?.toString() || u.tenantId?.toString() || 'platform';
    const tname = u.tenantId?.name || (u.scope === 'platform' ? 'Platform Units' : tid);
    if (!allTenantGroupMap[tid]) allTenantGroupMap[tid] = { name: tname, units: [] };
    allTenantGroupMap[tid].units.push(u);
  });
  const allGroups = Object.entries(allTenantGroupMap).sort((a, b) =>
    a[1].name.localeCompare(b[1].name)
  );
  const showTenantFilter = isPlatformStaff && allGroups.length >= 2;

  // ── Apply tenant + date filters for display tallies ─────────────────────────
  let displayUnits = (showTenantFilter && tenantFilter !== 'all')
    ? units.filter(u => {
        const tid = u.tenantId?._id?.toString() || u.tenantId?.toString() || 'platform';
        return tid === tenantFilter;
      })
    : [...units];
  if (dateFrom) {
    const from = new Date(dateFrom);
    displayUnits = displayUnits.filter(u => new Date(u.createdAt) >= from);
  }
  if (dateTo) {
    const to = new Date(dateTo);
    to.setHours(23, 59, 59, 999);
    displayUnits = displayUnits.filter(u => new Date(u.createdAt) <= to);
  }
  const hasAnyFilter = tenantFilter !== 'all' || !!dateFrom || !!dateTo;

  if (!units.length) {
    return (
      <div style={{ color:'#475569', fontSize:13, padding:'40px 0', textAlign:'center' }}>
        No land units to summarise. Create units first.
      </div>
    );
  }

  // ── Derived tallies (from filtered units) ─────────────────────────────────
  const total      = displayUnits.length;
  const assessed   = displayUnits.filter(u => u.latestRecommendation).length;
  const unassessed = total - assessed;
  const escalated  = displayUnits.filter(u => u.reviewStatus === 'escalated').length;
  const underReview = displayUnits.filter(u => u.reviewStatus === 'under_review').length;
  const attentionUnits = displayUnits.filter(u =>
    u.reviewStatus === 'escalated' || u.reviewStatus === 'under_review' || !u.latestRecommendation
  ).slice(0, 8);

  const evalTally = EVAL_TYPES.map(et => ({
    ...et,
    count: displayUnits.filter(u => u.latestRecommendation === et.value).length,
  })).filter(et => et.count > 0);

  const reviewTally = REVIEW_STATUSES.map(rs => ({
    ...rs,
    count: displayUnits.filter(u => (u.reviewStatus || 'open') === rs.value).length,
  })).filter(rs => rs.count > 0);

  const typeTally = UNIT_TYPES.map(t => ({
    ...t,
    icon: UNIT_TYPE_ICON[t.value] || '📍',
    count: displayUnits.filter(u => u.unitType === t.value).length,
  })).filter(t => t.count > 0)
    .sort((a, b) => b.count - a.count);

  // ── Export handlers (Phase 3u / 3v) ──────────────────────────────────────
  const exportSlug = tenantFilter !== 'all'
    ? (allGroups.find(([tid]) => tid === tenantFilter)?.[1]?.name || tenantFilter)
        .toLowerCase().replace(/\s+/g, '-')
    : 'all-communities';

  const handleLeadershipExport = () => {
    const md = buildLeadershipMarkdown(displayUnits, allGroups, tenantFilter, isPlatformStaff, dateFrom, dateTo);
    downloadFile(md, `cip-portfolio-summary-${exportSlug}.md`, 'text/markdown;charset=utf-8');
  };

  const handleCSVExport = () => {
    const csv = buildLeadershipCSV(displayUnits);
    downloadFile(csv, `cip-portfolio-${exportSlug}.csv`, 'text/csv;charset=utf-8');
  };


  return (
    <div style={{ fontSize:13, color:'#e2e8f0' }}>

      {/* ── Dashboard toolbar: filters + exports ── */}
      <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', marginBottom:16 }}>
        {/* Community filter */}
        {showTenantFilter && (
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1 }}>COMMUNITY</span>
            <select
              value={tenantFilter}
              onChange={e => onTenantFilterChange(e.target.value)}
              style={{
                background:'#1e293b', color:'#cbd5e1', border:'1px solid #334155',
                borderRadius:5, fontSize:12, padding:'4px 8px', cursor:'pointer',
              }}
            >
              <option value="all">All Communities ({units.length})</option>
              {allGroups.map(([tid, grp]) => (
                <option key={tid} value={tid}>{grp.name} ({grp.units.length})</option>
              ))}
            </select>
            {tenantFilter !== 'all' && (
              <button onClick={() => onTenantFilterChange('all')}
                style={{ background:'none', border:'none', color:'#475569', fontSize:11, cursor:'pointer', padding:'2px 4px' }}
              >✕</button>
            )}
          </div>
        )}
        {/* Date range filter */}
        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
          <span style={{ fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1 }}>FROM</span>
          <input type="date" value={dateFrom} onChange={e => onDateFromChange(e.target.value)}
            style={DB_STYLE.dateInput}
          />
          <span style={{ fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1 }}>TO</span>
          <input type="date" value={dateTo} onChange={e => onDateToChange(e.target.value)}
            style={DB_STYLE.dateInput}
          />
          {(dateFrom || dateTo) && (
            <button onClick={() => { onDateFromChange(''); onDateToChange(''); }}
              style={{ background:'none', border:'none', color:'#475569', fontSize:11, cursor:'pointer', padding:'2px 4px' }}
            >✕</button>
          )}
        </div>
        <div style={{ flex:1 }} />
        {/* Exports */}
        <button onClick={handleLeadershipExport}
          title="Download portfolio summary (.md)"
          style={DB_STYLE.btnMd}
        >📥 Export .md</button>
        <button onClick={handleCSVExport}
          title="Download portfolio data (.csv)"
          style={DB_STYLE.btnCsv}
        >📊 Export .csv</button>
      </div>

      {/* ── Stat tiles ── */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:4 }}>
        <DashTile label={`Total Units${hasAnyFilter ? ' (filtered)' : ''}`} value={total} color="#64748b" />
        <DashTile label="Assessed" value={assessed} color="#22c55e" sub={`${unassessed} without eval`} />
        <DashTile label="Escalated" value={escalated} color={escalated > 0 ? '#ef4444' : '#334155'} sub="needs urgent attention" />
        <DashTile label="Under Review" value={underReview} color={underReview > 0 ? '#f59e0b' : '#334155'} />
      </div>

      {/* ── Recommendation breakdown ── */}
      <DashSection label="RECOMMENDATION BREAKDOWN" />
      {evalTally.length > 0
        ? <DashSegBar
            items={[
              ...evalTally,
              ...(unassessed > 0 ? [{ value:'unassessed', label:'No eval', color:'#1e293b', count:unassessed }] : [])
            ]}
            onClick={(val) => onDrillEval && onDrillEval(val)}
          />
        : <div style={{ fontSize:12, color:'#334155', fontStyle:'italic' }}>No evaluations recorded yet.</div>
      }

      {/* ── Review status breakdown ── */}
      <DashSection label="REVIEW STATUS BREAKDOWN" />
      <DashSegBar items={reviewTally} onClick={(val) => onDrillReview && onDrillReview(val)} />

      {/* ── Unit type breakdown ── */}
      <DashSection label="UNIT TYPES" />
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        {typeTally.map(t => (
          <div key={t.value} style={{
            background:'#0f172a', borderRadius:6, padding:'6px 12px',
            display:'flex', gap:6, alignItems:'center',
          }}>
            <span>{t.icon}</span>
            <span style={{ fontSize:12, color:'#94a3b8' }}>{t.label}</span>
            <span style={{ fontSize:13, fontWeight:700, color:'#e2e8f0' }}>{t.count}</span>
          </div>
        ))}
      </div>

      {/* ── Units needing attention ── */}
      {attentionUnits.length > 0 && (
        <>
          <DashSection label={`NEEDS ATTENTION (${attentionUnits.length})`} />
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {attentionUnits.map(u => {
              const rs     = REVIEW_STATUSES.find(r => r.value === (u.reviewStatus || 'open'));
              const et     = u.latestRecommendation
                ? EVAL_TYPES.find(e => e.value === u.latestRecommendation)
                : null;
              const urgent = u.reviewStatus === 'escalated';
              return (
                <div
                  key={u._id}
                  onClick={() => onSelectUnit && onSelectUnit(u)}
                  style={{
                    background:'#0f172a', borderRadius:6, padding:'9px 12px',
                    borderLeft:`3px solid ${urgent ? '#ef4444' : !u.latestRecommendation ? '#334155' : '#f59e0b'}`,
                    cursor:'pointer', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap',
                  }}
                >
                  <span style={{ fontSize:13 }}>{UNIT_TYPE_ICON[u.unitType] || '📍'}</span>
                  <span style={{ fontSize:13, color:'#cbd5e1', flex:1, minWidth:80 }}>{u.name}</span>
                  <span style={{ fontSize:10, fontWeight:700, color: rs?.color || '#64748b',
                    background:(rs?.color || '#334155')+'22', borderRadius:3, padding:'1px 6px' }}>
                    {rs?.label || 'Open'}
                  </span>
                  {et
                    ? <span style={{ fontSize:10, color:et.color }}>{et.label}</span>
                    : <span style={{ fontSize:10, color:'#334155', fontStyle:'italic' }}>no eval</span>
                  }
                  <span style={{ fontSize:10, color:'#334155' }}>↗</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {attentionUnits.length === 0 && (
        <div style={{ marginTop:16, fontSize:13, color:'#22c55e', fontStyle:'italic' }}>
          ✓ No units currently need urgent attention.
        </div>
      )}

      {/* ── Cross-tenant breakdown (platform staff, all-communities view only) ── */}
      {isPlatformStaff && tenantFilter === 'all' && allGroups.length >= 2 && (() => {
        return (
          <>
            <DashSection label={`COMMUNITY BREAKDOWN (${allGroups.length} tenants)`} />
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {allGroups.map(([tid, grp]) => {
                const gc         = grp.units.length;
                const gEscalated = grp.units.filter(u => u.reviewStatus === 'escalated').length;
                const gNoEval    = grp.units.filter(u => !u.latestRecommendation).length;
                const gLatestRec = grp.units
                  .filter(u => u.latestRecommendation)
                  .reduce((acc, u) => {
                    acc[u.latestRecommendation] = (acc[u.latestRecommendation] || 0) + 1;
                    return acc;
                  }, {});
                const topRec = Object.entries(gLatestRec)
                  .sort((a, b) => b[1] - a[1])[0];
                const topEt  = topRec ? EVAL_TYPES.find(e => e.value === topRec[0]) : null;
                return (
                  <div
                    key={tid}
                    onClick={() => onTenantFilterChange(tid)}
                    style={{
                      background:'#0f172a', borderRadius:7, padding:'10px 14px',
                      borderLeft:`3px solid ${gEscalated > 0 ? '#ef4444' : '#1e3a5f'}`,
                      cursor:'pointer',
                    }}
                    title={`Click to filter dashboard to ${grp.name}`}
                  >
                    <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ fontSize:13, fontWeight:700, color:'#e2e8f0', flex:1, minWidth:100 }}>
                        {grp.name}
                      </span>
                      <span style={{ fontSize:11, color:'#64748b' }}>{gc} unit{gc !== 1 ? 's' : ''}</span>
                      {gEscalated > 0 && (
                        <span style={{ fontSize:10, fontWeight:700, color:'#ef4444',
                          background:'#ef444422', borderRadius:3, padding:'1px 6px' }}>
                          ⚠ {gEscalated} escalated
                        </span>
                      )}
                      {gNoEval > 0 && (
                        <span style={{ fontSize:10, color:'#475569',
                          background:'#1e293b', borderRadius:3, padding:'1px 6px' }}>
                          {gNoEval} unassessed
                        </span>
                      )}
                      {topEt && (
                        <span style={{ fontSize:10, color:topEt.color }}>
                          top: {topEt.label} ×{topRec[1]}
                        </span>
                      )}
                      <span style={{ fontSize:10, color:'#334155' }}>→ filter</span>
                    </div>
                    {/* Mini eval bar */}
                    {gc > 0 && (() => {
                      const gAssessed = grp.units.filter(u => u.latestRecommendation);
                      return gAssessed.length > 0 ? (
                        <div style={{ display:'flex', height:4, borderRadius:3, overflow:'hidden',
                          marginTop:8, background:'#1e293b' }}>
                          {EVAL_TYPES.map(et => {
                            const c = gAssessed.filter(u => u.latestRecommendation === et.value).length;
                            if (!c) return null;
                            return (
                              <div key={et.value}
                                style={{ width:`${(c / gc) * 100}%`, background:et.color }}
                                title={`${et.label}: ${c}`}
                              />
                            );
                          })}
                          {gNoEval > 0 && (
                            <div style={{ width:`${(gNoEval / gc) * 100}%`, background:'#1e293b',
                              border:'1px solid #334155' }}
                              title={`Unassessed: ${gNoEval}`}
                            />
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function LandUnitsPage() {
  const { activeTenant, user } = useAuth();
  const tenantCtx = useTenantSafe();
  const tenantId = tenantCtx?.viewTenant?._id || activeTenant?._id;
  const isPlatformStaff = tenantCtx?.isPlatformStaff || ['cip_admin', 'cip_staff'].includes(user?.globalRole);
  const isManager = canStaffSection(user, tenantId, 'landUnits');

  const [searchParams, setSearchParams] = useUrlParams({
    view: 'list', type: '', eval: '', review: '', dateFrom: '', dateTo: '', tenant: 'all',
  });
  // Destructure with local names that match the rest of the page
  const view         = ['list', 'map', 'dashboard'].includes(searchParams.view) ? searchParams.view : 'list';
  const typeFilter   = searchParams.type;
  const evalFilter   = searchParams.eval;
  const reviewFilter = searchParams.review;
  const dateFrom     = searchParams.dateFrom;
  const dateTo       = searchParams.dateTo;
  const tenantFilter = searchParams.tenant;
  const setView         = (v) => setSearchParams('view',    v);
  const setTypeFilter   = (v) => setSearchParams('type',    v);
  const setEvalFilter   = (v) => setSearchParams('eval',    v);
  const setReviewFilter = (v) => setSearchParams('review',  v);
  const setDateFrom     = (v) => setSearchParams('dateFrom', v);
  const setDateTo       = (v) => setSearchParams('dateTo',   v);
  const setTenantFilter = (v) => setSearchParams('tenant',  v);

  const [units,        setUnits]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showCreate,   setShowCreate]   = useState(false);
  const [sortBy,       setSortBy]       = useState('name'); // 'name' | 'recommendation' | 'reviewStatus'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (!isPlatformStaff && tenantId) params.tenantId = tenantId;
      if (typeFilter) params.unitType = typeFilter;
      const res = await getLandUnits(params);
      setUnits(res.data);
    } catch { setUnits([]); } finally { setLoading(false); }
  }, [tenantId, typeFilter, isPlatformStaff]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (unit) => {
    setShowCreate(false);
    setUnits(prev => [unit, ...prev]);
    setSelectedUnit(unit);
  };

  // ── Client-side filtering + sorting ─────────────────────────────────────────
  const EVAL_ORDER = ['unsuitable', 'consult_further', 'suitable_conditions', 'monitor', 'preserve', 'no_concerns', ''];
  const REVIEW_ORDER = ['escalated', 'under_review', 'reviewed', 'open'];

  const filteredUnits = units
    .filter(u => {
      if (!evalFilter) return true;
      if (evalFilter === 'unassessed') return !u.latestRecommendation;
      return u.latestRecommendation === evalFilter;
    })
    .filter(u => !reviewFilter || u.reviewStatus === reviewFilter)
    .sort((a, b) => {
      if (sortBy === 'recommendation') {
        const ai = EVAL_ORDER.indexOf(a.latestRecommendation || '');
        const bi = EVAL_ORDER.indexOf(b.latestRecommendation || '');
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      }
      if (sortBy === 'reviewStatus') {
        const ai = REVIEW_ORDER.indexOf(a.reviewStatus);
        const bi = REVIEW_ORDER.indexOf(b.reviewStatus);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      }
      return (a.name || '').localeCompare(b.name || '');
    });

  const hasActiveFilter = typeFilter || evalFilter || reviewFilter;
  const community = filteredUnits.filter(u => u.scope === 'community');
  const platform  = filteredUnits.filter(u => u.scope === 'platform');

  const viewBtnStyle = (active) => ({
    background: active ? '#1e3a5f' : '#1e293b',
    border: `1px solid ${active ? '#3b82f6' : '#334155'}`,
    color: active ? '#7dd3fc' : '#64748b',
    borderRadius: 5, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  });

  return (
    <div style={{ padding:'24px 28px' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h2 style={{ color:'#f1f5f9', margin:0, fontSize:22, fontWeight:700 }}>🗾 Land Units</h2>
          <p style={{ color:'#64748b', fontSize:13, margin:'4px 0 0' }}>
            Named geographic areas for structured factor observations and analysis.
          </p>
        </div>
        {isManager && !showCreate && (
          <button onClick={() => setShowCreate(true)} style={{
            background:'#1d4ed8', border:'none', borderRadius:7, color:'#fff',
            padding:'9px 18px', cursor:'pointer', fontSize:13, fontWeight:600 }}>
            + New Unit
          </button>
        )}
      </div>

      {/* Create form */}
      {showCreate && (
        <CreateUnitForm
          tenantId={tenantId}
          isPlatformStaff={isPlatformStaff}
          onCreated={handleCreated}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Toolbar: filters + sort + view toggle */}
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{
          background:'#1e293b', border:'1px solid #334155', borderRadius:6,
          color:'#e2e8f0', padding:'6px 10px', fontSize:12, cursor:'pointer' }}>
          <option value="">All types</option>
          {UNIT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {isManager && (
          <select value={reviewFilter} onChange={e => setReviewFilter(e.target.value)} style={{
            background:'#1e293b', border:'1px solid #334155', borderRadius:6,
            color:'#e2e8f0', padding:'6px 10px', fontSize:12, cursor:'pointer' }}>
            <option value="">All review states</option>
            {REVIEW_STATUSES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        )}
        {isManager && (
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
            background:'#1e293b', border:'1px solid #334155', borderRadius:6,
            color:'#e2e8f0', padding:'6px 10px', fontSize:12, cursor:'pointer' }}>
            <option value="name">Sort: Name</option>
            <option value="recommendation">Sort: Recommendation</option>
            <option value="reviewStatus">Sort: Review Status</option>
          </select>
        )}
        <div style={{ display:'flex', gap:4, marginLeft:'auto' }}>
          <button style={viewBtnStyle(view === 'list')}      onClick={() => setView('list')}>☰ List</button>
          <button style={viewBtnStyle(view === 'map')}       onClick={() => setView('map')}>🗺 Map</button>
          <button style={viewBtnStyle(view === 'dashboard')} onClick={() => setView('dashboard')}>📊 Dashboard</button>
        </div>
      </div>

      {/* Active filter chips — visible when arriving pre-filtered (e.g. from Executive Rollup) */}
      {hasActiveFilter && (() => {
        const et = evalFilter === 'unassessed'
          ? { label: 'No Evaluation', color: '#64748b' }
          : evalFilter ? EVAL_TYPES.find(e => e.value === evalFilter) : null;
        const rs = reviewFilter ? REVIEW_STATUSES.find(r => r.value === reviewFilter) : null;
        const tt = typeFilter ? UNIT_TYPES.find(t => t.value === typeFilter) : null;
        const chips = [
          tt && { label: `Type: ${tt.label}`,   color: '#0ea5e9', onRemove: () => setTypeFilter('') },
          et && { label: `Eval: ${et.label}`,   color: et.color,  onRemove: () => setEvalFilter('') },
          rs && { label: `Status: ${rs.label}`, color: rs.color,  onRemove: () => setReviewFilter('') },
        ].filter(Boolean);
        return (
          <FilterChipBar
            chips={chips}
            hint={`${filteredUnits.length} of ${units.length} units`}
            onClearAll={() => { setTypeFilter(''); setEvalFilter(''); setReviewFilter(''); }}
          />
        );
      })()}

      {/* Recommendation tally bar (managers only) */}
      {isManager && !loading && units.length > 0 && (
        <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
          <span style={{ fontSize:10, color:'#334155', fontWeight:700, letterSpacing:0.5 }}>PORTFOLIO</span>
          {(() => {
            const c = units.filter(u => !u.latestRecommendation).length;
            if (c === 0) return null;
            const active = evalFilter === 'unassessed';
            return (
              <button key="unassessed" onClick={() => setEvalFilter(active ? '' : 'unassessed')} style={{
                background: active ? '#1e293b' : '#0f172a',
                border: `1px solid ${active ? '#64748b' : '#1e293b'}`,
                color: active ? '#94a3b8' : '#475569',
                borderRadius: 12, padding:'2px 9px', cursor:'pointer', fontSize:10, fontWeight:600,
              }}>○ No eval ×{c}</button>
            );
          })()}
          {EVAL_TYPES.map(et => {
            const c = units.filter(u => u.latestRecommendation === et.value).length;
            if (c === 0) return null;
            const active = evalFilter === et.value;
            return (
              <button key={et.value} onClick={() => setEvalFilter(active ? '' : et.value)} style={{
                background: active ? et.color + '22' : '#0f172a',
                border: `1px solid ${active ? et.color : '#1e293b'}`,
                color: active ? et.color : '#475569',
                borderRadius: 12, padding:'2px 9px', cursor:'pointer', fontSize:10, fontWeight:600,
              }}>● {et.label} ×{c}</button>
            );
          })}
          {hasActiveFilter && (
            <span style={{ fontSize:10, color:'#475569', marginLeft:4 }}>
              — showing {filteredUnits.length} of {units.length}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ color:'#64748b', padding:'40px 0', textAlign:'center' }}>Loading land units…</div>
      ) : (
        <>
          {/* ── DASHBOARD VIEW ── */}
          {view === 'dashboard' && (
            <LandPortfolioDashboard
              units={units}
              isManager={isManager}
              isPlatformStaff={isPlatformStaff}
              dateFrom={dateFrom}           onDateFromChange={setDateFrom}
              dateTo={dateTo}               onDateToChange={setDateTo}
              tenantFilter={tenantFilter}   onTenantFilterChange={setTenantFilter}
              onDrillEval={(val) => { setEvalFilter(val); setView('list'); }}
              onDrillReview={(val) => { setReviewFilter(val); setView('list'); }}
              onSelectUnit={(u) => { setSelectedUnit(u); setView('list'); }}
            />
          )}

          {/* ── MAP VIEW ── */}
          {view === 'map' && (
            <div>
              <LandUnitMap
                units={units}
                selectedUnit={selectedUnit}
                onUnitClick={u => setSelectedUnit(u)}
                height="450px"
              />
              {!selectedUnit && units.length > 0 && (
                <div style={{ marginTop:12, color:'#475569', fontSize:13, textAlign:'center' }}>
                  Click a unit on the map to view its factor breakdown and score snapshot.
                </div>
              )}
              {/* Quick-select chip strip under the map */}
              {filteredUnits.length > 0 && (
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:10 }}>
                  {filteredUnits.map(u => (
                    <button key={u._id} onClick={() => setSelectedUnit(u)} style={{
                      background: selectedUnit?._id === u._id ? '#1e3a5f' : '#1e293b',
                      border: `1px solid ${selectedUnit?._id === u._id ? '#3b82f6' : '#334155'}`,
                      color: selectedUnit?._id === u._id ? '#7dd3fc' : '#94a3b8',
                      borderRadius: 6, padding:'5px 10px', cursor:'pointer', fontSize:12,
                    }}>
                      {UNIT_TYPE_ICON[u.unitType] || '📍'} {u.name}
                    </button>
                  ))}
                </div>
              )}
              {selectedUnit && (
                <div style={{ marginTop:14 }}>
                  <UnitDetail
                    key={selectedUnit._id}
                    unit={selectedUnit}
                    tenantId={tenantId}
                    isManager={isManager}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── LIST VIEW ── */}
          {view === 'list' && (
            <div style={{ display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap' }}>
              {/* Unit list */}
              <div style={{ flex:'0 0 340px', minWidth:280 }}>
                {community.length > 0 && (
                  <>
                    <div style={{ fontSize:11, color:'#475569', fontWeight:700,
                      letterSpacing:1, marginBottom:6 }}>COMMUNITY UNITS</div>
                    {community.map(u => (
                      <UnitCard key={u._id} unit={u} isManager={isManager}
                        onSelect={() => setSelectedUnit(u)}
                        isSelected={selectedUnit?._id === u._id} />
                    ))}
                  </>
                )}
                {platform.length > 0 && (
                  <>
                    <div style={{ fontSize:11, color:'#7c3aed', fontWeight:700,
                      letterSpacing:1, margin:'14px 0 6px' }}>PLATFORM UNITS</div>
                    {platform.map(u => (
                      <UnitCard key={u._id} unit={u} isManager={isManager}
                        onSelect={() => setSelectedUnit(u)}
                        isSelected={selectedUnit?._id === u._id} />
                    ))}
                  </>
                )}
                {units.length === 0 && (
                  <div style={{ color:'#475569', fontSize:13, padding:'30px 0', textAlign:'center' }}>
                    No land units found.
                    {isManager && !showCreate && (
                      <div style={{ marginTop:8, fontSize:12 }}>
                        Create the first unit with "+ New Unit".
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Detail panel */}
              {selectedUnit ? (
                <UnitDetail
                  key={selectedUnit._id}
                  unit={selectedUnit}
                  tenantId={tenantId}
                  isManager={isManager}
                />
              ) : (
                <div style={{ flex:1, background:'#1e293b', borderRadius:10, padding:30,
                  textAlign:'center', color:'#475569', fontSize:14, minHeight:200,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  Select a land unit to view its factor breakdown and score snapshot
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
